import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminBot from "../components/AdminBot";
import QuestionBankTab from "../components/QuestionBankTab";
import { 
  BookOpen, Plus, Trash2, ArrowLeft, Loader2, Home, Settings,
  FolderPlus, FileText, Sparkles, Upload, CheckCircle, XCircle,
  ChevronRight, Edit2, RefreshCw, Lock, Layers, BookMarked, FileDown,
  CircleHelp, Pause, Play, AlertCircle, Zap, DollarSign, Clock, 
  Activity, StopCircle, BarChart3, Brain, GraduationCap, FileUp,
  Bell, Send, Image, Link as LinkIcon, Users, Globe, MessageCircle,
  Eye, Ban, Phone, Mail, Shield, UserPlus, Database, Key, Search, Copy
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const LMS_PASSWORD = "Viaan@123";

// ─── Payments Sync Component ───────────────────────────────────────────────
function PaymentsSyncTab() {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState("missing"); // "all" | "missing" | "synced"
  const [selected, setSelected] = useState(new Set());
  const [progress, setProgress] = useState("");

  const fetchPayments = async () => {
    setLoading(true);
    setResults([]);
    try {
      const res = await axios.get(`${API}/admin/razorpay/payments?admin_key=${LMS_PASSWORD}`);
      setPayments(res.data.payments || []);
      setStats({ total: res.data.total_razorpay, synced: res.data.synced, missing: res.data.missing });
      // Auto-select all missing
      const missingIds = (res.data.payments || []).filter(p => p.status === "missing").map(p => p.razorpay_payment_id);
      setSelected(new Set(missingIds));
    } catch (e) {
      alert("Failed to fetch payments: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const provisionSelected = async () => {
    if (selected.size === 0) return;
    setProvisioning(true);
    setResults([]);
    setProgress(`Provisioning ${selected.size} students...`);
    try {
      const res = await axios.post(`${API}/admin/razorpay/provision`, {
        admin_key: LMS_PASSWORD,
        payment_ids: [...selected],
        days: 365,
      });
      setResults(res.data.results || []);
      setProgress(`Done! ${res.data.provisioned} provisioned, ${res.data.failed} failed.`);
      // Refresh
      await fetchPayments();
    } catch (e) {
      setProgress("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setProvisioning(false);
    }
  };

  const filtered = payments.filter(p => filter === "all" || p.status === filter);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-orange-400" /> Razorpay Payment Sync
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Find students who paid but were not provisioned — create accounts &amp; send credentials automatically
          </p>
        </div>
        <Button onClick={fetchPayments} disabled={loading} className="bg-orange-600 hover:bg-orange-700">
          {loading ? "Fetching..." : "Fetch from Razorpay"}
        </Button>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Total Captured", value: stats.total, color: "blue" },
            { label: "Already Synced", value: stats.synced, color: "green" },
            { label: "Missing / Unsynced", value: stats.missing, color: "orange" },
          ].map(s => (
            <div key={s.label} className={`bg-${s.color}-500/10 border border-${s.color}-500/30 rounded-lg p-4 text-center`}>
              <div className={`text-3xl font-bold text-${s.color}-400`}>{s.value}</div>
              <div className="text-slate-400 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Actions Bar */}
      {payments.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-2">
            {["all", "missing", "synced"].map(f => (
              <Button key={f} size="sm" variant={filter === f ? "default" : "outline"}
                onClick={() => setFilter(f)} className={filter === f ? "bg-slate-600" : ""}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </Button>
            ))}
          </div>
          <span className="text-slate-400 text-sm">{selected.size} selected</span>
          <Button size="sm" variant="outline"
            onClick={() => setSelected(new Set(payments.filter(p => p.status === "missing").map(p => p.razorpay_payment_id)))}>
            Select All Missing
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}>Clear</Button>
          {selected.size > 0 && (
            <Button onClick={provisionSelected} disabled={provisioning}
              className="bg-green-600 hover:bg-green-700 ml-auto" data-testid="provision-btn">
              {provisioning ? "Provisioning..." : `Provision ${selected.size} Students`}
            </Button>
          )}
        </div>
      )}

      {/* Progress */}
      {progress && (
        <div className={`p-3 rounded-lg text-sm font-medium ${provisioning ? "bg-blue-500/10 text-blue-300" : "bg-green-500/10 text-green-300"}`}>
          {progress}
        </div>
      )}

      {/* Provision Results */}
      {results.length > 0 && (
        <div className="bg-slate-800/50 rounded-xl border border-white/10 p-4 space-y-2 max-h-64 overflow-y-auto">
          <h3 className="text-white font-semibold text-sm mb-2">Provision Results</h3>
          {results.map((r, i) => (
            <div key={i} className={`flex items-center gap-3 text-xs p-2 rounded ${r.status === "success" ? "bg-green-500/10 text-green-300" : "bg-red-500/10 text-red-300"}`}>
              <span>{r.status === "success" ? "✓" : "✗"}</span>
              <span className="font-medium">{r.name || "—"}</span>
              <span className="text-slate-400">{r.mobile} · {r.email}</span>
              {r.action === "created" && <Badge className="bg-blue-500/20 text-blue-300 text-[10px]">New Account</Badge>}
              {r.email_sent && <Badge className="bg-purple-500/20 text-purple-300 text-[10px]">Email Sent</Badge>}
              {r.status === "error" && <span className="text-red-400">{r.reason}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Payment Table */}
      {filtered.length > 0 ? (
        <div className="bg-slate-800/50 rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-slate-700/30">
                <th className="p-3 text-left w-8"><input type="checkbox"
                  checked={filtered.every(p => selected.has(p.razorpay_payment_id))}
                  onChange={e => {
                    if (e.target.checked) setSelected(prev => new Set([...prev, ...filtered.map(p => p.razorpay_payment_id)]));
                    else setSelected(prev => { const n = new Set(prev); filtered.forEach(p => n.delete(p.razorpay_payment_id)); return n; });
                  }} /></th>
                <th className="p-3 text-left text-slate-300">Payment ID</th>
                <th className="p-3 text-left text-slate-300">Email / Mobile</th>
                <th className="p-3 text-left text-slate-300">Amount</th>
                <th className="p-3 text-left text-slate-300">Date</th>
                <th className="p-3 text-left text-slate-300">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.razorpay_payment_id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                  onClick={() => p.status === "missing" && toggleSelect(p.razorpay_payment_id)}>
                  <td className="p-3">
                    {p.status === "missing" && (
                      <input type="checkbox" checked={selected.has(p.razorpay_payment_id)}
                        onChange={() => toggleSelect(p.razorpay_payment_id)} onClick={e => e.stopPropagation()} />
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs text-slate-300">{p.razorpay_payment_id}</td>
                  <td className="p-3">
                    <div className="text-white text-xs">{p.email || "—"}</div>
                    <div className="text-slate-400 text-xs">{p.mobile || "—"}</div>
                  </td>
                  <td className="p-3 text-green-400 font-medium">₹{p.amount_inr}</td>
                  <td className="p-3 text-slate-400 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN") : "—"}</td>
                  <td className="p-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${p.status === "synced" ? "bg-green-500/20 text-green-400" : "bg-orange-500/20 text-orange-400"}`}>
                      {p.status === "synced" ? "Synced" : "Missing"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : payments.length === 0 && !loading ? (
        <div className="text-center py-16 text-slate-500">
          <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Click "Fetch from Razorpay" to load payment data</p>
        </div>
      ) : null}
    </div>
  );
}

// ─── Mock Tests Admin Tab ────────────────────────────────────────────────────
function MockTestsTab() {
  const [examType, setExamType] = useState("tgt");
  const [examPattern, setExamPattern] = useState("kvs");
  const [subject, setSubject] = useState("mathematics");
  const [progress, setProgress] = useState([]);
  const [mocks, setMocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedMock, setSelectedMock] = useState(null);
  const [mockQuestions, setMockQuestions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [dedupRunning, setDedupRunning] = useState(false);
  const [dedupResult, setDedupResult] = useState(null);
  const [clearingStuck, setClearingStuck] = useState(false);
  const [completingIncomplete, setCompletingIncomplete] = useState(false);
  const [incompleteResult, setIncompleteResult] = useState(null);
  const [objDedupRunning, setObjDedupRunning] = useState(false);
  const [objDedupResult, setObjDedupResult] = useState(null);
  const [objDedupSubject, setObjDedupSubject] = useState("");
  // Quality Check state
  const [qcRunning, setQcRunning] = useState(false);
  const [qcResult, setQcResult] = useState(null);
  const [qcSource, setQcSource] = useState("objective"); // "objective" or "mock"
  const [qcSubject, setQcSubject] = useState("");

  // Fix Descriptions state
  const [fixDescRunning, setFixDescRunning] = useState(false);
  const [fixDescResult, setFixDescResult] = useState(null);
  const [fixDescJobId, setFixDescJobId] = useState(null);
  const [qcHistoryOpen, setQcHistoryOpen] = useState(false);
  const [qcHistory, setQcHistory] = useState(null);
  const [qcHistoryLoading, setQcHistoryLoading] = useState(false);
  const qcPollRef = useRef(null);
  const pollRef = useRef(null);
  const dedupPollRef = useRef(null);
  const objDedupPollRef = useRef(null);

  const subjects = {
    prt: ["hindi", "english", "mathematics", "evs", "general_knowledge", "pedagogy", "child_development"],
    tgt: ["hindi", "english", "mathematics", "science", "social_science", "computer_science", "physical_education", "music", "art"],
    pgt: ["hindi", "english", "mathematics", "physics", "chemistry", "biology", "commerce", "economics", "geography", "history", "computer_science", "music", "art"],
  };

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/mock-tests/admin/progress?admin_key=${LMS_PASSWORD}&exam_type=${examType}`);
      setProgress(res.data.progress || []);
    } catch { }
  };

  const fetchMocks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mock-tests/admin/list?admin_key=${LMS_PASSWORD}&exam_type=${examType}&exam_pattern=${examPattern}&subject=${subject}`);
      setMocks(res.data.mocks || []);
    } catch (e) {
      toast.error("Failed to fetch mocks");
    }
    setLoading(false);
  };

  useEffect(() => { fetchProgress(); }, [examType]);
  useEffect(() => { fetchMocks(); }, [examType, examPattern, subject]);

  // Poll progress during generation
  useEffect(() => {
    if (generating) {
      pollRef.current = setInterval(() => { fetchProgress(); fetchMocks(); }, 8000);
      return () => clearInterval(pollRef.current);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [generating]);

  const handleBulkDelete = async () => {
    const targetIds = selectedIds;
    const confirmMsg = targetIds.length === 0
      ? `Delete ALL mock tests for ${subject} (${examPattern.toUpperCase()})? This cannot be undone.`
      : `Delete ${targetIds.length} selected mock tests? This cannot be undone.`;
    if (!confirm(confirmMsg)) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/mock-tests/admin/bulk-delete`, {
        admin_key: LMS_PASSWORD,
        exam_type: examType,
        exam_pattern: examPattern,
        subject,
        mock_ids: targetIds,
      });
      toast.success(`Deleted ${targetIds.length || "all"} mock tests.`);
      setSelectedIds([]);
      fetchMocks();
      fetchProgress();
    } catch (e) {
      toast.error("Delete failed: " + (e.response?.data?.detail || e.message));
    }
    setDeleting(false);
  };

  const toggleSelectMock = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const readyIds = mocks.filter(m => m.status === "ready").map(m => m.id);
    setSelectedIds(prev => prev.length === readyIds.length ? [] : readyIds);
  };

  const handleBulkGenerate = async () => {
    if (!confirm(`Generate ALL 10 mock tests for ${subject} (${examPattern.toUpperCase()})? This will take ~20-30 minutes.`)) return;
    setGenerating(true);
    try {
      await axios.post(`${API}/mock-tests/admin/bulk-generate`, {
        admin_key: LMS_PASSWORD, exam_type: examType, exam_pattern: examPattern, subject,
      });
      toast.success("Bulk generation started! Progress will update automatically.");
    } catch (e) {
      toast.error("Bulk generation failed: " + (e.response?.data?.detail || e.message));
      setGenerating(false);
    }
  };

  const handleSingleGenerate = async (mockNum) => {
    try {
      await axios.post(`${API}/mock-tests/admin/generate`, {
        admin_key: LMS_PASSWORD, exam_type: examType, exam_pattern: examPattern, subject, mock_number: mockNum,
      });
      toast.success(`Generating Mock Test ${mockNum}...`);
      setGenerating(true);
    } catch (e) {
      toast.error("Generation failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleViewQuestions = async (mockId) => {
    try {
      const res = await axios.get(`${API}/mock-tests/admin/${mockId}?admin_key=${LMS_PASSWORD}`);
      setMockQuestions(res.data.questions || []);
      setSelectedMock(mockId);
    } catch (e) {
      toast.error("Failed to load questions");
    }
  };

  const handleDedupClean = async () => {
    if (!confirm(`Clean duplicate questions across ALL mock tests for ${examType.toUpperCase()}?\n\nThis will:\n1. Scan ALL mocks across all subjects and patterns\n2. Find repeated questions\n3. Replace duplicates with new diverse-topic questions\n\nThis may take several minutes.`)) return;
    setDedupRunning(true);
    setDedupResult(null);
    try {
      await axios.post(`${API}/mock-tests/admin/dedup-clean`, {
        admin_key: LMS_PASSWORD, exam_type: examType,
      });
      toast.success("Global dedup cleaning started! Progress will update below.");
      dedupPollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/mock-tests/admin/dedup-progress?admin_key=${LMS_PASSWORD}&exam_type=${examType}`);
          setDedupResult(res.data);
          if (res.data.status === "done" || res.data.status === "error") {
            clearInterval(dedupPollRef.current);
            setDedupRunning(false);
            fetchMocks();
            if (res.data.status === "done") toast.success("Dedup cleaning complete!");
            else toast.error("Dedup cleaning failed");
          }
        } catch { }
      }, 5000);
    } catch (e) {
      toast.error("Dedup clean failed: " + (e.response?.data?.detail || e.message));
      setDedupRunning(false);
    }
  };

  useEffect(() => {
    return () => { if (dedupPollRef.current) clearInterval(dedupPollRef.current); };
  }, []);

  const handleClearStuck = async () => {
    if (!confirm("Clear all stuck generation states? This will allow students to re-trigger any stuck mock tests.")) return;
    setClearingStuck(true);
    try {
      const res = await axios.post(`${API}/mock-tests/admin/clear-stuck`, {
        admin_key: LMS_PASSWORD, exam_type: examType,
      });
      toast.success(`Cleared ${res.data.cleared} stuck items`);
      fetchMocks();
      fetchProgress();
    } catch (e) {
      toast.error("Clear failed: " + (e.response?.data?.detail || e.message));
    }
    setClearingStuck(false);
  };

  const handleCompleteIncomplete = async () => {
    if (!confirm(`Scan ALL mock tests for ${examType.toUpperCase()} and complete any with missing questions?\n\nThis will trigger AI generation only for incomplete tests.`)) return;
    setCompletingIncomplete(true);
    setIncompleteResult(null);
    try {
      const res = await axios.post(`${API}/mock-tests/admin/complete-incomplete`, {
        admin_key: LMS_PASSWORD, exam_type: examType,
      });
      setIncompleteResult(res.data);
      if (res.data.incomplete_found === 0) {
        toast.success("All mock tests are complete! No action needed.");
      } else {
        toast.success(`Found ${res.data.incomplete_found} incomplete tests. Generation started!`);
        fetchMocks();
        fetchProgress();
      }
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    }
    setCompletingIncomplete(false);
  };

  // Check existing dedup progress on mount/filter change
  useEffect(() => {
    const checkDedupProgress = async () => {
      try {
        const res = await axios.get(`${API}/mock-tests/admin/dedup-progress?admin_key=${LMS_PASSWORD}&exam_type=${examType}`);
        if (res.data.status === "running") {
          setDedupRunning(true);
          setDedupResult(res.data);
          dedupPollRef.current = setInterval(async () => {
            try {
              const r = await axios.get(`${API}/mock-tests/admin/dedup-progress?admin_key=${LMS_PASSWORD}&exam_type=${examType}`);
              setDedupResult(r.data);
              if (r.data.status === "done" || r.data.status === "error" || r.data.status === "paused" || r.data.status === "idle") {
                clearInterval(dedupPollRef.current);
                setDedupRunning(false);
                fetchMocks();
              }
            } catch { }
          }, 5000);
        } else if (res.data.status === "done" || res.data.status === "error" || res.data.status === "paused") {
          setDedupResult(res.data);
          setDedupRunning(false);
        } else {
          setDedupResult(null);
          setDedupRunning(false);
        }
      } catch {
        setDedupResult(null);
      }
    };
    if (dedupPollRef.current) clearInterval(dedupPollRef.current);
    checkDedupProgress();
  }, [examType]);
  
  const handleDedupReset = async () => {
    if (!confirm("Reset the stuck dedup process? This won't undo any changes already made - it just allows you to restart.")) return;
    try {
      const res = await axios.post(`${API}/mock-tests/admin/dedup-reset`, {
        admin_key: LMS_PASSWORD, exam_type: examType,
      });
      toast.success(res.data.message);
      setDedupRunning(false);
      setDedupResult(null);
      if (dedupPollRef.current) clearInterval(dedupPollRef.current);
    } catch (e) {
      toast.error("Reset failed: " + (e.response?.data?.detail || e.message));
    }
  };

  // ── Objective Question Dedup ─────────────────────────────────────────────
  const [objDedupStuck, setObjDedupStuck] = useState(false);

  const checkObjDedupProgress = async () => {
    try {
      const res = await axios.get(
        `${API}/question-bank/admin/clean-duplicates-progress?admin_key=${LMS_PASSWORD}&exam_type=${examType}&subject=${objDedupSubject}`
      );
      const d = res.data;
      if (d.status === "running" || d.auto_resuming) {
        setObjDedupRunning(true);
        setObjDedupResult(d);
        // If auto-resuming, clear stuck state
        if (d.auto_resuming) {
          setObjDedupStuck(false);
        } else if (d.updated_at) {
          const lastUpdate = new Date(d.updated_at);
          const staleMs = Date.now() - lastUpdate.getTime();
          // Only show stuck if > 8 min AND backend hasn't already auto-resumed
          setObjDedupStuck(staleMs > 8 * 60 * 1000);
        }
      } else if (d.status === "done" || d.status === "error") {
        setObjDedupRunning(false);
        setObjDedupStuck(false);
        setObjDedupResult(d);
        if (objDedupPollRef.current) clearInterval(objDedupPollRef.current);
      } else {
        setObjDedupRunning(false);
        setObjDedupStuck(false);
      }
    } catch { }
  };

  useEffect(() => {
    if (objDedupPollRef.current) clearInterval(objDedupPollRef.current);
    checkObjDedupProgress();
    objDedupPollRef.current = setInterval(checkObjDedupProgress, 5000);
    return () => { if (objDedupPollRef.current) clearInterval(objDedupPollRef.current); };
  }, [examType, objDedupSubject]);

  const handleObjDedupClean = async () => {
    if (!confirm(`Start duplicate scan for ${examType.toUpperCase()} objective questions${objDedupSubject ? ` (${objDedupSubject})` : ' (all subjects)'}?\n\nThis will:\n1. Load all questions from the question bank\n2. Find duplicates using Jaccard similarity math\n3. Replace duplicates with new AI-generated questions\n\nThis may take several minutes.`)) return;
    try {
      const res = await axios.post(`${API}/question-bank/admin/clean-duplicates`, {
        admin_key: LMS_PASSWORD, exam_type: examType, subject: objDedupSubject,
      });
      if (res.data.success) {
        toast.success("Objective question dedup started!");
        setObjDedupRunning(true);
        setObjDedupStuck(false);
        setObjDedupResult({ summary: "Starting scan...", status: "running" });
        if (objDedupPollRef.current) clearInterval(objDedupPollRef.current);
        objDedupPollRef.current = setInterval(checkObjDedupProgress, 5000);
      } else {
        toast.error(res.data.message || "Could not start");
      }
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleObjDedupResume = async () => {
    try {
      const res = await axios.post(`${API}/question-bank/admin/resume-duplicates`, {
        admin_key: LMS_PASSWORD, exam_type: examType, subject: objDedupSubject,
      });
      if (res.data.success) {
        toast.success(res.data.message);
        setObjDedupRunning(true);
        setObjDedupStuck(false);
        setObjDedupResult(prev => ({ ...(prev || {}), status: "running", summary: res.data.message }));
        if (objDedupPollRef.current) clearInterval(objDedupPollRef.current);
        objDedupPollRef.current = setInterval(checkObjDedupProgress, 5000);
      } else {
        // No saved pending list — show message with option to restart fresh
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error("Resume failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleObjDedupReset = async () => {
    if (!confirm("Reset the stuck objective dedup process?")) return;
    try {
      const res = await axios.post(`${API}/question-bank/admin/clean-duplicates-reset`, {
        admin_key: LMS_PASSWORD, exam_type: examType, subject: objDedupSubject,
      });
      toast.success(res.data.message);
      setObjDedupRunning(false);
      setObjDedupResult(null);
      if (objDedupPollRef.current) clearInterval(objDedupPollRef.current);
    } catch (e) {
      toast.error("Reset failed: " + (e.response?.data?.detail || e.message));
    }
  };

  // ── Quality Check Handlers ────────────────────────────────────────────────
  const checkQCProgress = async () => {
    try {
      const res = await axios.get(
        `${API}/question-bank/admin/quality-check-status?admin_key=${LMS_PASSWORD}&exam_type=${examType}&subject=${qcSubject}&source=${qcSource}`
      );
      const d = res.data;
      if (d.status === "running" || d.auto_resuming) {
        setQcRunning(true);
        setQcResult(d);
      } else if (d.status === "done" || d.status === "error") {
        setQcRunning(false);
        setQcResult(d);
        if (qcPollRef.current) clearInterval(qcPollRef.current);
      } else {
        setQcRunning(false);
      }
    } catch { }
  };

  useEffect(() => {
    if (qcPollRef.current) clearInterval(qcPollRef.current);
    checkQCProgress();
    qcPollRef.current = setInterval(checkQCProgress, 6000);
    return () => { if (qcPollRef.current) clearInterval(qcPollRef.current); };
  }, [examType, qcSource, qcSubject]);

  const handleStartQC = async () => {
    const sourceLabel = qcSource === "objective" ? "Objective Practice MCQs" : "Mock Test MCQs";
    const subjectLabel = qcSubject || "all subjects";
    if (!confirm(
      `Start Quality Check for ${sourceLabel} (${examType.toUpperCase()} / ${subjectLabel})?\n\n` +
      `The AI will:\n` +
      `1. Ask "Is this answer correct?" for each question (YES/NO)\n` +
      `2. If wrong → ask "What is the correct answer?"\n` +
      `3. Verify again — if confirmed twice → replace in database\n\n` +
      `This may take several minutes depending on question count.`
    )) return;

    try {
      const res = await axios.post(`${API}/question-bank/admin/quality-check`, {
        admin_key: LMS_PASSWORD, exam_type: examType, subject: qcSubject, source: qcSource,
      });
      if (res.data.success) {
        toast.success("Quality Check started!");
        setQcRunning(true);
        setQcResult({ summary: "Starting verification...", status: "running", stats: {} });
        if (qcPollRef.current) clearInterval(qcPollRef.current);
        qcPollRef.current = setInterval(checkQCProgress, 6000);
      } else {
        toast.error(res.data.message || "Could not start QC");
      }
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleQCReset = async () => {
    if (!confirm("Reset the Quality Check process?")) return;
    try {
      const res = await axios.post(`${API}/question-bank/admin/quality-check-reset`, {
        admin_key: LMS_PASSWORD, exam_type: examType, subject: qcSubject, source: qcSource,
      });
      toast.success(res.data.message);
      setQcRunning(false);
      setQcResult(null);
      if (qcPollRef.current) clearInterval(qcPollRef.current);
    } catch (e) {
      toast.error("Reset failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleViewHistory = async () => {
    setQcHistoryLoading(true);
    setQcHistoryOpen(true);
    try {
      const res = await axios.get(
        `${API}/question-bank/admin/quality-check-history?admin_key=${LMS_PASSWORD}&exam_type=${examType}&subject=${qcSubject}&source=${qcSource}`
      );
      setQcHistory(res.data);
    } catch (e) {
      toast.error("Could not load history: " + (e.response?.data?.detail || e.message));
    } finally {
      setQcHistoryLoading(false);
    }
  };

  useEffect(() => {
    return () => { if (qcPollRef.current) clearInterval(qcPollRef.current); };
  }, []);

  // ── Fix Descriptions Handlers ─────────────────────────────────────────────
  const fixDescPollRef = useRef(null);
  const stopFixDescPoll = () => { if (fixDescPollRef.current) { clearInterval(fixDescPollRef.current); fixDescPollRef.current = null; } };

  const pollFixDescStatus = async (jid) => {
    try {
      const res = await axios.get(`${API}/question-bank/admin/fix-descriptions/status?admin_key=${LMS_PASSWORD}&job_id=${jid}`);
      setFixDescResult(res.data);
      if (res.data.status === "completed" || res.data.status === "error") {
        setFixDescRunning(false);
        stopFixDescPoll();
      }
    } catch { stopFixDescPoll(); }
  };

  const handleFixDescriptions = async () => {
    const src = qcSource;
    const subj = qcSubject || "all subjects";
    if (!window.confirm(
      `Regenerate EXPLANATION for every question in ${examType.toUpperCase()} / ${subj} (${src})?\n\n` +
      `This uses AI to write explanations matching the current correct answer.\n` +
      `Large subjects may take several minutes.`
    )) return;

    setFixDescRunning(true);
    setFixDescResult(null);
    stopFixDescPoll();
    try {
      const res = await axios.post(
        `${API}/question-bank/admin/fix-descriptions/start?admin_key=${LMS_PASSWORD}&exam_type=${examType}&subject=${qcSubject}&source=${src}`
      );
      const jid = res.data.job_id;
      setFixDescJobId(jid);
      fixDescPollRef.current = setInterval(() => pollFixDescStatus(jid), 5000);
      toast.success("Fix Descriptions job started!");
    } catch (e) {
      setFixDescRunning(false);
      toast.error("Failed to start: " + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => () => stopFixDescPoll(), []);

  // Get progress for current subject/pattern
  const currentProgress = progress.find(p => p.exam_pattern === examPattern && p.subject === subject);
  const generatedCount = currentProgress?.generated || 0;
  const generatingMock = currentProgress?.generating_mock;

  const handleResumeMock = async (mockId) => {
    try {
      const res = await axios.post(`${API}/mock-tests/admin/resume`, {
        admin_key: LMS_PASSWORD, mock_id: mockId,
      });
      toast.success(`Resuming from ${res.data.saved_questions} saved questions`);
      fetchMocks();
      fetchProgress();
    } catch (e) {
      toast.error("Resume failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-rose-400" /> Full Mock Tests
          </h2>
          <p className="text-slate-400 text-sm mt-1">Create and manage subject-wise mock tests (stored in Firebase)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select value={examType} onChange={e => { setExamType(e.target.value); setSubject(subjects[e.target.value]?.[0] || "mathematics"); }}
          className="bg-slate-800 border border-slate-600 text-white px-3 py-2 rounded-lg text-sm">
          <option value="prt">PRT</option>
          <option value="tgt">TGT</option>
          <option value="pgt">PGT</option>
        </select>
        <select value={examPattern} onChange={e => setExamPattern(e.target.value)}
          className="bg-slate-800 border border-slate-600 text-white px-3 py-2 rounded-lg text-sm">
          <option value="kvs">KVS (70Q, 2.5h)</option>
          {examType !== "prt" && <option value="emrs">EMRS (55Q, 3h)</option>}
        </select>
        <select value={subject} onChange={e => setSubject(e.target.value)}
          className="bg-slate-800 border border-slate-600 text-white px-3 py-2 rounded-lg text-sm">
          {(subjects[examType] || []).map(s => (
            <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
        <Button onClick={handleBulkGenerate} disabled={generating}
          className="bg-rose-600 hover:bg-rose-700 text-white">
          {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Bulk Generate (10)</>}
        </Button>
        <Button
          onClick={handleBulkDelete}
          disabled={deleting || mocks.filter(m => m.status === "ready").length === 0}
          variant="outline"
          className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
          data-testid="bulk-delete-mock-tests-btn"
        >
          {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" />{selectedIds.length > 0 ? `Delete (${selectedIds.length})` : "Delete All"}</>}
        </Button>
        <Button
          onClick={handleClearStuck}
          disabled={clearingStuck}
          variant="outline"
          className="border-amber-600 text-amber-400 hover:bg-amber-600 hover:text-white"
          data-testid="clear-stuck-btn"
        >
          {clearingStuck ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Clearing...</> : <><StopCircle className="w-4 h-4 mr-2" />Clear Stuck</>}
        </Button>
        <Button
          onClick={handleCompleteIncomplete}
          disabled={completingIncomplete}
          variant="outline"
          className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
          data-testid="complete-incomplete-btn"
        >
          {completingIncomplete ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</> : <><CheckCircle className="w-4 h-4 mr-2" />Complete Incomplete</>}
        </Button>
      </div>
      {/* Progress Bar */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-white">
            {subject.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} — {examPattern.toUpperCase()} Progress
          </span>
          <span className="text-sm text-slate-400">{generatedCount}/10 mocks ready</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-3">
          <div className="bg-gradient-to-r from-rose-500 to-pink-500 h-3 rounded-full transition-all" style={{ width: `${(generatedCount / 10) * 100}%` }} />
        </div>
        {generatingMock && <p className="text-xs text-amber-400 mt-2 animate-pulse">Generating Mock Test {generatingMock}...</p>}
        {currentProgress?.error && <p className="text-xs text-red-400 mt-2">Error: {currentProgress.error}</p>}
      </div>

      {/* Subject-wise Progress Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(subjects[examType] || []).map(s => {
          const p = progress.find(x => x.exam_pattern === examPattern && x.subject === s);
          const cnt = p?.generated || 0;
          return (
            <div key={s} className={`bg-slate-800/50 rounded-lg p-3 border cursor-pointer transition-all ${subject === s ? "border-rose-500" : "border-slate-700 hover:border-slate-500"}`}
              onClick={() => setSubject(s)}>
              <p className="text-xs text-slate-400 truncate">{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
              <p className={`text-lg font-bold ${cnt === 10 ? "text-green-400" : cnt > 0 ? "text-amber-400" : "text-slate-500"}`}>{cnt}/10</p>
            </div>
          );
        })}
      </div>

      {/* Mock Tests List */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Mock Tests ({examPattern.toUpperCase()})</h3>
          {mocks.filter(m => m.status === "ready").length > 0 && (
            <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-rose-500 cursor-pointer"
                checked={selectedIds.length > 0 && selectedIds.length === mocks.filter(m => m.status === "ready").length}
                onChange={toggleSelectAll}
                data-testid="select-all-mocks-checkbox"
              />
              Select All
            </label>
          )}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Array.from({ length: 10 }, (_, i) => i + 1).map(num => {
              const mock = mocks.find(m => m.mock_number === num);
              const status = mock?.status || "not_created";
              return (
                <div key={num} className={`bg-slate-800/50 rounded-lg p-4 border transition-all flex items-center justify-between ${selectedIds.includes(mock?.id) ? "border-rose-500" : "border-slate-700"}`}>
                  <div className="flex items-center gap-3">
                    {mock?.status === "ready" && (
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-rose-500 cursor-pointer"
                        checked={selectedIds.includes(mock.id)}
                        onChange={() => toggleSelectMock(mock.id)}
                        data-testid={`mock-checkbox-${num}`}
                      />
                    )}
                    <div>
                      <p className="font-semibold text-white">Mock Test {num}</p>
                      <p className="text-xs text-slate-400">
                        {status === "ready" ? `${mock.total_questions} questions` : status === "generating" ? "Generating..." : status === "paused" ? `Paused — ${mock.total_questions || mock.questions?.length || 0} Qs saved` : "Not created"}
                      </p>
                      {status === "paused" && mock.pause_reason && (
                        <p className="text-xs text-amber-400 mt-0.5">{mock.pause_reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {status === "ready" ? (
                      <Button size="sm" variant="outline" onClick={() => handleViewQuestions(mock.id)}
                        className="border-slate-600 text-slate-300 text-xs"><Eye className="w-3 h-3 mr-1" />View</Button>
                    ) : status === "generating" ? (
                      <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
                    ) : status === "paused" ? (
                      <Button size="sm" onClick={() => handleResumeMock(mock.id)}
                        className="bg-amber-600 hover:bg-amber-700 text-xs" data-testid={`resume-mock-${num}`}>
                        <Play className="w-3 h-3 mr-1" />Resume
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => handleSingleGenerate(num)}
                        className="bg-blue-600 hover:bg-blue-700 text-xs"><Sparkles className="w-3 h-3 mr-1" />Generate</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Question Viewer */}
      {selectedMock && mockQuestions.length > 0 && (
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Questions — {selectedMock}</h3>
            <Button size="sm" variant="ghost" onClick={() => { setSelectedMock(null); setMockQuestions([]); }} className="text-slate-400">Close</Button>
          </div>
          <div className="max-h-[600px] overflow-y-auto space-y-3">
            {mockQuestions.map((q, i) => (
              <div key={i} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${q.type === "objective" ? "bg-blue-900/50 text-blue-300" : "bg-purple-900/50 text-purple-300"}`}>
                    Q{q.q_num} - {q.type}
                  </span>
                </div>
                <p className="text-sm text-white mb-1">{q.question_en}</p>
                <p className="text-sm text-slate-400">{q.question_hi}</p>
                {q.type === "objective" && q.options_en && (
                  <div className="mt-2 space-y-1">
                    {q.options_en.map((opt, oi) => (
                      <p key={oi} className={`text-xs ${oi === q.correct_answer ? "text-green-400 font-semibold" : "text-slate-500"}`}>
                        {String.fromCharCode(65 + oi)}. {opt}
                      </p>
                    ))}
                  </div>
                )}
                {q.type === "descriptive" && q.ideal_answer_en && (
                  <details className="mt-2">
                    <summary className="text-xs text-green-400 cursor-pointer">Ideal Answer</summary>
                    <p className="text-xs text-slate-400 mt-1">{q.ideal_answer_en}</p>
                  </details>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



// ── Language Quality Scanner Component ──────────────────────────────────────
function LanguageQualityScanner({ lmsPassword, API }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const pollStatus = async (jid) => {
    try {
      const res = await axios.get(`${API}/admin/notes-language-scan/status?admin_key=${lmsPassword}&job_id=${jid}`);
      setStatus(res.data);
      if (res.data.status === "completed" || res.data.status === "error") stopPolling();
    } catch { stopPolling(); }
  };

  const fetchLatest = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/notes-language-scan/status?admin_key=${lmsPassword}`);
      setStatus(res.data);
      if (res.data.job_id && res.data.status === "running") {
        setJobId(res.data.job_id);
        pollRef.current = setInterval(() => pollStatus(res.data.job_id), 5000);
      }
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { fetchLatest(); return stopPolling; }, []);

  const startScan = async (dryRun) => {
    if (!window.confirm(dryRun ? "Run DRY SCAN? (no deletions, just report)" : "⚠️ This will DELETE all mismatched notes. Are you sure?")) return;
    setLoading(true);
    stopPolling();
    try {
      const res = await axios.post(`${API}/admin/notes-language-scan`, { admin_key: lmsPassword, dry_run: dryRun });
      const jid = res.data.job_id;
      setJobId(jid);
      pollRef.current = setInterval(() => pollStatus(jid), 4000);
    } catch (e) { alert("Failed to start scan: " + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };

  const isDone = status?.status === "completed";
  const isRunning = status?.status === "running";
  const mismatchList = status?.mismatch_list || [];

  return (
    <Card className="bg-white/5 border-white/10 mt-6" data-testid="language-scanner-card">
      <CardHeader>
        <CardTitle className="text-lg text-amber-300 flex items-center gap-2">
          <span>Language Quality Scanner</span>
          {isRunning && <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full animate-pulse">Scanning...</span>}
          {isDone && <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Done</span>}
        </CardTitle>
        <p className="text-sm text-slate-400">
          Scan all notes and detect ones that don't match their stored language (English notes with Hindi content or vice versa).
          Thresholds: English notes with &gt;25% Devanagari chars are flagged. Hindi notes with &lt;10% Devanagari chars are flagged.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats row */}
        {status && status.status !== "not_started" && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-center">
              <p className="text-xs text-blue-300 mb-0.5">Scanned</p>
              <p className="text-xl font-bold text-white">{status.scanned ?? 0}</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
              <p className="text-xs text-red-300 mb-0.5">Mismatched</p>
              <p className="text-xl font-bold text-white">{status.mismatched ?? 0}</p>
            </div>
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
              <p className="text-xs text-green-300 mb-0.5">Deleted</p>
              <p className="text-xl font-bold text-white">{status.deleted ?? 0}</p>
            </div>
          </div>
        )}

        {/* Status info */}
        {status && (
          <div className="text-xs text-slate-400 space-y-0.5">
            {status.started_at && <p>Started: {new Date(status.started_at).toLocaleString("en-IN")}</p>}
            {status.completed_at && <p>Completed: {new Date(status.completed_at).toLocaleString("en-IN")}</p>}
            {status.dry_run !== undefined && <p>Mode: {status.dry_run ? "Dry Run (no deletions)" : "Delete Mode"}</p>}
            {status.error && <p className="text-red-400">Error: {status.error}</p>}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => startScan(true)}
            disabled={loading || isRunning}
            className="bg-amber-600 hover:bg-amber-500 text-white text-sm"
            data-testid="lang-scan-dry-run-btn"
          >
            {isRunning ? "Scanning..." : "Dry Scan (Report Only)"}
          </Button>
          {isDone && (status?.mismatched ?? 0) > 0 && status?.dry_run && (
            <Button
              onClick={() => startScan(false)}
              disabled={loading || isRunning}
              className="bg-red-700 hover:bg-red-600 text-white text-sm"
              data-testid="lang-scan-delete-btn"
            >
              Delete {status.mismatched} Mismatched Notes
            </Button>
          )}
          <Button
            onClick={fetchLatest}
            disabled={loading}
            variant="outline"
            className="border-slate-600 text-slate-300 text-sm"
          >
            Refresh
          </Button>
        </div>

        {/* Mismatch list */}
        {mismatchList.length > 0 && (
          <div className="mt-2 space-y-2 max-h-80 overflow-y-auto">
            <p className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
              Mismatched Notes ({mismatchList.length} shown):
            </p>
            {mismatchList.map((m, i) => (
              <div key={i} className="bg-red-900/20 border border-red-500/30 rounded-lg p-2.5 text-xs">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-yellow-400 font-bold uppercase">{m.exam_type}</span>
                  <span className="text-white">·</span>
                  <span className="text-blue-300">{m.subject}</span>
                  <span className="text-white">·</span>
                  <span className="text-white font-medium">{m.topic}</span>
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold ${m.language === 'english' ? 'bg-red-700/40 text-red-300' : 'bg-orange-700/40 text-orange-300'}`}>
                    stored: {m.language} | Devanagari: {(m.deva_ratio * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="text-slate-400 italic truncate">{m.preview}</p>
              </div>
            ))}
          </div>
        )}

        {isDone && mismatchList.length === 0 && (
          <p className="text-sm text-green-400 text-center py-3">
            All notes respect their language setting!
          </p>
        )}
      </CardContent>
    </Card>
  );
}


export default function LMSManager() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem('lms_manager_authenticated') === 'true');
  const [password, setPassword] = useState("");
  
  // Data states
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [topics, setTopics] = useState([]);
  // Topic index states
  const [topicIndexes, setTopicIndexes] = useState({});  // {topicName: indexText}
  const [editingTopicIndex, setEditingTopicIndex] = useState(null);  // topic name being edited
  const [savingTopicIndex, setSavingTopicIndex] = useState(null);  // topic being saved
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [showAddExam, setShowAddExam] = useState(false);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [showAddTopics, setShowAddTopics] = useState(false);
  const [showGenerateTopics, setShowGenerateTopics] = useState(false);
  const [showExtractTopics, setShowExtractTopics] = useState(false);  // New: AI Extract from image/text
  const [showRenameExam, setShowRenameExam] = useState(false);
  const [showRenameSubject, setShowRenameSubject] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [newDisplayName, setNewDisplayName] = useState("");
  
  // Form states
  const [newExamId, setNewExamId] = useState("");
  const [newExamName, setNewExamName] = useState("");
  const [newSubjectId, setNewSubjectId] = useState("");
  const [newSubjectName, setNewSubjectName] = useState("");
  const [topicsText, setTopicsText] = useState("");
  const [aiDescription, setAiDescription] = useState("");
  const [aiTopicCount, setAiTopicCount] = useState(20);
  const [generating, setGenerating] = useState(false);
  const [generatedTopics, setGeneratedTopics] = useState([]);
  
  // AI Extract Topics states
  const [extractText, setExtractText] = useState("");
  const [extractImage, setExtractImage] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedTopics, setExtractedTopics] = useState([]);

  // PDF Bulk Generation states
  const [pdfStats, setPdfStats] = useState(null);
  const [pdfJobId, setPdfJobId] = useState({});
  const [pdfJobStatus, setPdfJobStatus] = useState({});
  const [pdfGenerating, setPdfGenerating] = useState({});
  const [pdfPaused, setPdfPaused] = useState({});

  // Batch API Job Management states (MongoDB-backed for persistence)
  const [batchJobs, setBatchJobs] = useState([]);
  const [batchJobsSummary, setBatchJobsSummary] = useState(null);
  const [batchJobsLoading, setBatchJobsLoading] = useState(false);
  const [creatingBatchJob, setCreatingBatchJob] = useState(false);
  const [showBatchJobDialog, setShowBatchJobDialog] = useState(false);
  const [batchJobForm, setBatchJobForm] = useState({ 
    exam_type: '', 
    subjects: [],  
    language: 'english',
    mode: 'fast'  // 'fast' (Vertex AI) or 'cost_saver' (Gemini Batch API)
  });
  const [batchSubjectOptions, setBatchSubjectOptions] = useState([]);
  const [activeTab, setActiveTab] = useState("syllabus");
  // Chat passcode gate
  const [chatPasscodeVerified, setChatPasscodeVerified] = useState(false);
  const [chatPasscodeInput, setChatPasscodeInput] = useState('');
  const [chatPasscodeError, setChatPasscodeError] = useState(false);
  const CHAT_PC = btoa('508969'); // obfuscated, not visible in plain text
  const pollIntervalRef = useRef(null);

  // Cache Management State
  const [cacheStats, setCacheStats] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [deleteCode, setDeleteCode] = useState("");
  const [deleteFilter, setDeleteFilter] = useState({ exam_type: "", subject: "", topic: "" });
  const [deleteResult, setDeleteResult] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Question Bank State
  const [qbStats, setQbStats] = useState(null);
  const [qbTopics, setQbTopics] = useState([]);
  const [qbLoading, setQbLoading] = useState(false);
  const [qbPresetTopics, setQbPresetTopics] = useState([]);
  const [scrapeJobId, setScrapeJobId] = useState(null);
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [scrapeForm, setScrapeForm] = useState({ topic_slug: "", topic_name: "", subject: "pedagogy", url: "" });
  const [scrapeMode, setScrapeMode] = useState("preset"); // "preset" or "custom"
  
  // Push Notification State
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    body: '',
    notification_type: 'text', // text, link, link_image
    link_url: '',
    image_url: '',
    target_audience: 'all' // all, free, paid
  });
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [notificationStats, setNotificationStats] = useState(null);
  
  // User Password Management State
  const [userMobile, setUserMobile] = useState('');
  const [userData, setUserData] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Student List Filters
  const [studentFilters, setStudentFilters] = useState({
    exam_category: '',
    subject: ''
  });
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [showStudentList, setShowStudentList] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  // AI Training State
  const [trainingData, setTrainingData] = useState([]);
  const [trainingStats, setTrainingStats] = useState(null);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [uploadingTraining, setUploadingTraining] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [trainingForm, setTrainingForm] = useState({
    exam_type: '',
    subject: '',
    material_type: 'pyq', // 'pyq' or 'notes'
    title: ''
  });
  const [trainingFile, setTrainingFile] = useState(null);
  const [trainingSubjectOptions, setTrainingSubjectOptions] = useState([]);
  
  // NCERT Exemplar State
  const [ncertExamType, setNcertExamType] = useState('');
  const [ncertSubject, setNcertSubject] = useState('');
  const [ncertClass, setNcertClass] = useState('');
  const [ncertClasses, setNcertClasses] = useState([]);
  const [ncertSubjectOptions, setNcertSubjectOptions] = useState([]);
  const [ncertQuestions, setNcertQuestions] = useState([]);
  const [ncertStats, setNcertStats] = useState(null);
  const [ncertLoading, setNcertLoading] = useState(false);
  const [showNCERTExtractDialog, setShowNCERTExtractDialog] = useState(false);
  const [ncertExtractText, setNcertExtractText] = useState('');
  const [extractedQuestions, setExtractedQuestions] = useState([]);
  const [extractingNCERT, setExtractingNCERT] = useState(false);
  const [fetchingNCERT, setFetchingNCERT] = useState(false);
  
  // Settings State
  const [llmProvider, setLlmProvider] = useState("auto");
  const [updatingProvider, setUpdatingProvider] = useState(false);

  // Team Management State
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ mobile: '', name: '', role: 'member' });
  const [addingMember, setAddingMember] = useState(false);

  // Chat Monitoring State
  const [activeChats, setActiveChats] = useState([]);
  const [chatStats, setChatStats] = useState(null);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatTimeRange, setChatTimeRange] = useState(24);

  // Support Chat Dashboard State
  const [supportStats, setSupportStats] = useState(null);
  const [supportPeriod, setSupportPeriod] = useState("daily");
  const [supportStatsLoading, setSupportStatsLoading] = useState(false);

  // Fetch Team Members
  const fetchTeamMembers = useCallback(async () => {
    setTeamLoading(true);
    try {
      const res = await axios.get(`${API}/admin/team/list?admin_key=${LMS_PASSWORD}`);
      setTeamMembers(res.data.team_members || []);
    } catch (err) {
      console.error("Failed to fetch team members:", err);
    } finally {
      setTeamLoading(false);
    }
  }, []);

  // Add Team Member
  const handleAddTeamMember = async () => {
    if (!newMember.mobile || !newMember.name) {
      toast.error("Please fill all fields");
      return;
    }
    setAddingMember(true);
    try {
      await axios.post(`${API}/admin/team/add`, {
        ...newMember,
        admin_key: LMS_PASSWORD
      });
      toast.success(`Added ${newMember.name} as ${newMember.role}`);
      setShowAddMember(false);
      setNewMember({ mobile: '', name: '', role: 'member' });
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add member");
    } finally {
      setAddingMember(false);
    }
  };

  // Update Team Member
  const handleUpdateRole = async (mobile, newRole) => {
    try {
      await axios.post(`${API}/admin/team/update`, {
        mobile,
        role: newRole,
        admin_key: LMS_PASSWORD
      });
      toast.success("Role updated");
      fetchTeamMembers();
    } catch (err) {
      toast.error("Failed to update role");
    }
  };

  // Remove Team Member
  const handleRemoveMember = async (mobile, name) => {
    if (!window.confirm(`Remove ${name} from team?`)) return;
    try {
      await axios.delete(`${API}/admin/team/remove?mobile=${mobile}&admin_key=${LMS_PASSWORD}`);
      toast.success("Member removed");
      fetchTeamMembers();
    } catch (err) {
      toast.error("Failed to remove member");
    }
  };

  // Fetch Active Chats
  const fetchActiveChats = useCallback(async () => {
    setChatsLoading(true);
    try {
      const [chatsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/chats/active?admin_key=${LMS_PASSWORD}&hours=${chatTimeRange}`),
        axios.get(`${API}/admin/chats/stats?admin_key=${LMS_PASSWORD}`)
      ]);
      setActiveChats(chatsRes.data.active_chats || []);
      setChatStats(statsRes.data);
    } catch (err) {
      console.error("Failed to fetch chats:", err);
    } finally {
      setChatsLoading(false);
    }
  }, [chatTimeRange]);

  const fetchSupportStats = useCallback(async (period) => {
    setSupportStatsLoading(true);
    try {
      const { data } = await axios.get(`${window.location.origin}/api/support-chat/admin/dashboard?passcode=12345678&period=${period}`);
      setSupportStats(data);
    } catch (err) {
      console.error("Failed to fetch support stats:", err);
    } finally {
      setSupportStatsLoading(false);
    }
  }, []);

  // Fetch LLM Provider Config
  const fetchLLMConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notes/config/llm-provider`);
      setLlmProvider(res.data.provider || "auto");
    } catch (err) {
      console.error("Failed to fetch LLM config:", err);
    }
  }, []);

  // Update LLM Provider
  const updateLLMProvider = async (provider) => {
    setUpdatingProvider(true);
    try {
      await axios.post(`${API}/notes/config/llm-provider`, { provider });
      setLlmProvider(provider);
      toast.success(`Frontend provider set to: ${provider.toUpperCase()}`);
    } catch (err) {
      toast.error("Failed to update provider");
    } finally {
      setUpdatingProvider(false);
    }
  };

  // Fetch subjects for batch job dialog when exam changes
  const fetchBatchSubjects = useCallback(async (examId) => {
    if (!examId) {
      setBatchSubjectOptions([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/notes/lms/syllabus/exams/${examId}/subjects`);
      setBatchSubjectOptions(res.data.subjects || []);
    } catch (err) {
      console.error("Failed to fetch subjects for batch:", err);
      setBatchSubjectOptions([]);
    }
  }, []);

  // Fetch subjects for training dialog when exam changes
  const fetchTrainingSubjects = useCallback(async (examId) => {
    if (!examId) {
      setTrainingSubjectOptions([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/notes/lms/syllabus/exams/${examId}/subjects`);
      setTrainingSubjectOptions(res.data.subjects || []);
    } catch (err) {
      console.error("Failed to fetch subjects for training:", err);
      setTrainingSubjectOptions([]);
    }
  }, []);

  // Fetch training data list
  const fetchTrainingData = useCallback(async () => {
    setTrainingLoading(true);
    try {
      const res = await axios.get(`${API}/notes/lms/training/list`);
      setTrainingData(res.data.items || []);
      setTrainingStats(res.data.stats || null);
    } catch (err) {
      console.error("Failed to fetch training data:", err);
      toast.error("Failed to load training data");
    } finally {
      setTrainingLoading(false);
    }
  }, []);

  // Upload training material
  const uploadTrainingMaterial = async () => {
    if (!trainingFile || !trainingForm.exam_type || !trainingForm.subject || !trainingForm.title) {
      toast.error("Please fill all fields and select a file");
      return;
    }

    setUploadingTraining(true);
    try {
      const formData = new FormData();
      formData.append("file", trainingFile);
      formData.append("exam_type", trainingForm.exam_type);
      formData.append("subject", trainingForm.subject);
      formData.append("material_type", trainingForm.material_type);
      formData.append("title", trainingForm.title);

      const res = await axios.post(`${API}/notes/lms/training/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success(res.data.message || "Training material uploaded!");
      setShowUploadDialog(false);
      setTrainingFile(null);
      setTrainingForm({ exam_type: '', subject: '', material_type: 'pyq', title: '' });
      fetchTrainingData();

      // Auto-start extraction
      if (res.data.id) {
        await axios.post(`${API}/notes/lms/training/extract/${res.data.id}`);
        toast.info("Content extraction started...");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingTraining(false);
    }
  };

  // Delete training material
  const deleteTrainingMaterial = async (id) => {
    if (!window.confirm("Delete this training material?")) return;
    try {
      await axios.delete(`${API}/notes/lms/training/${id}`);
      toast.success("Training material deleted");
      fetchTrainingData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Extract content from training material
  const extractTrainingContent = async (id) => {
    try {
      const res = await axios.post(`${API}/notes/lms/training/extract/${id}`);
      toast.success(res.data.message || "Extraction started");
      // Refresh after a delay
      setTimeout(fetchTrainingData, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Extraction failed");
    }
  };

  // Extract all pending
  const extractAllPending = async () => {
    try {
      const res = await axios.post(`${API}/notes/lms/training/extract-all`);
      toast.success(res.data.message || "Batch extraction started");
      setTimeout(fetchTrainingData, 5000);
    } catch (err) {
      toast.error("Failed to start batch extraction");
    }
  };

  // Fetch training data when tab changes
  useEffect(() => {
    if (isAuthenticated && activeTab === "training") {
      fetchTrainingData();
    }
  }, [isAuthenticated, activeTab, fetchTrainingData]);

  // Update training subjects when exam changes
  useEffect(() => {
    if (trainingForm.exam_type) {
      fetchTrainingSubjects(trainingForm.exam_type);
    } else {
      setTrainingSubjectOptions([]);
    }
  }, [trainingForm.exam_type, fetchTrainingSubjects]);

  // ===== PUSH NOTIFICATION FUNCTIONS =====
  
  const fetchNotificationStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/stats?admin_key=${LMS_PASSWORD}`);
      setNotificationStats(res.data);
    } catch (err) {
      console.error("Failed to fetch notification stats:", err);
    }
  }, []);

  const fetchNotificationHistory = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const res = await axios.get(`${API}/notifications/history?admin_key=${LMS_PASSWORD}&limit=20`);
      setNotificationHistory(res.data.notifications || []);
    } catch (err) {
      console.error("Failed to fetch notification history:", err);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  const sendNotification = async () => {
    if (!notificationForm.title.trim() || !notificationForm.body.trim()) {
      toast.error("Title and message are required");
      return;
    }
    
    if (notificationForm.notification_type !== 'text' && !notificationForm.link_url.trim()) {
      toast.error("Link URL is required for this notification type");
      return;
    }
    
    if (notificationForm.notification_type === 'link_image' && !notificationForm.image_url.trim()) {
      toast.error("Image URL is required for this notification type");
      return;
    }

    setSendingNotification(true);
    try {
      const res = await axios.post(`${API}/notifications/send?admin_key=${LMS_PASSWORD}`, notificationForm);
      toast.success(`Notification sent to ${res.data.sent_count} devices!`);
      setNotificationForm({
        title: '',
        body: '',
        notification_type: 'text',
        link_url: '',
        image_url: '',
        target_audience: 'all'
      });
      fetchNotificationHistory();
      fetchNotificationStats();
    } catch (err) {
      toast.error("Failed to send notification: " + (err.response?.data?.detail || err.message));
    } finally {
      setSendingNotification(false);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/delete/${notificationId}?admin_key=${LMS_PASSWORD}`);
      toast.success("Notification deleted");
      fetchNotificationHistory();
    } catch (err) {
      toast.error("Failed to delete notification");
    }
  };

  // Load notifications when tab is active
  useEffect(() => {
    if (isAuthenticated && activeTab === "notifications") {
      fetchNotificationStats();
      fetchNotificationHistory();
    }
  }, [isAuthenticated, activeTab, fetchNotificationStats, fetchNotificationHistory]);

  // Fetch Team data when tab is accessed
  useEffect(() => {
    if (isAuthenticated && activeTab === "team") {
      fetchTeamMembers();
    }
  }, [isAuthenticated, activeTab, fetchTeamMembers]);

  // Fetch Chat data when tab is accessed
  useEffect(() => {
    if (isAuthenticated && activeTab === "chats") {
      fetchActiveChats();
      fetchSupportStats(supportPeriod);
    }
  }, [isAuthenticated, activeTab, fetchActiveChats, fetchSupportStats, supportPeriod]);

  // Fetch cache stats when tab is active
  useEffect(() => {
    if (isAuthenticated && activeTab === "cache") {
      fetchCacheStats();
    }
  }, [isAuthenticated, activeTab]);

  const fetchCacheStats = async () => {
    setCacheLoading(true);
    try {
      const res = await axios.get(`${API}/admin/notes-cache-stats`);
      setCacheStats(res.data);
    } catch (err) {
      console.error("Failed to fetch cache stats:", err);
    }
    setCacheLoading(false);
  };

  const handleBulkDelete = async () => {
    if (deleteCode !== "508969") {
      setDeleteResult({ success: false, error: "Invalid code. Enter 508969 to confirm." });
      return;
    }
    setDeleting(true);
    setDeleteResult(null);
    try {
      const res = await axios.post(`${API}/admin/bulk-delete-notes`, {
        code: deleteCode,
        exam_type: deleteFilter.exam_type,
        subject: deleteFilter.subject,
        topic: deleteFilter.topic,
      });
      setDeleteResult(res.data);
      fetchCacheStats(); // Refresh stats
      setDeleteCode("");
    } catch (err) {
      setDeleteResult({ success: false, error: err.response?.data?.detail || err.message });
    }
    setDeleting(false);
  };



  // ===== NCERT EXEMPLAR FUNCTIONS =====
  
  // Fetch NCERT classes when exam type changes
  const fetchNcertClasses = useCallback(async (examType) => {
    if (!examType) {
      setNcertClasses([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/notes/ncert/classes/${examType}`);
      setNcertClasses(res.data.classes || []);
    } catch (err) {
      console.error("Failed to fetch NCERT classes:", err);
      setNcertClasses([]);
    }
  }, []);
  
  // Fetch NCERT subjects when exam changes
  const fetchNcertSubjects = useCallback(async (examType) => {
    if (!examType) {
      setNcertSubjectOptions([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/notes/lms/syllabus/exams/${examType}/subjects`);
      setNcertSubjectOptions(res.data.subjects || []);
    } catch (err) {
      console.error("Failed to fetch subjects for NCERT:", err);
      setNcertSubjectOptions([]);
    }
  }, []);
  
  // Fetch NCERT questions
  const fetchNcertQuestions = useCallback(async () => {
    if (!ncertExamType || !ncertSubject) return;
    setNcertLoading(true);
    try {
      let url = `${API}/notes/ncert/questions?exam_type=${ncertExamType}&subject=${ncertSubject}`;
      if (ncertClass) url += `&class_level=${ncertClass}`;
      const res = await axios.get(url);
      setNcertQuestions(res.data.questions || []);
    } catch (err) {
      toast.error("Failed to load NCERT questions");
    } finally {
      setNcertLoading(false);
    }
  }, [ncertExamType, ncertSubject, ncertClass]);
  
  // Fetch NCERT stats
  const fetchNcertStats = useCallback(async () => {
    if (!ncertExamType || !ncertSubject) return;
    try {
      const res = await axios.get(`${API}/notes/ncert/stats/${ncertExamType}/${ncertSubject}`);
      setNcertStats(res.data);
    } catch (err) {
      console.error("Failed to fetch NCERT stats:", err);
    }
  }, [ncertExamType, ncertSubject]);
  
  // Extract questions from text using AI
  const extractNcertQuestionsFromText = async () => {
    if (!ncertExtractText.trim() || !ncertExamType || !ncertSubject || !ncertClass) {
      toast.error("Please fill all fields and paste text");
      return;
    }
    setExtractingNCERT(true);
    try {
      const res = await axios.post(`${API}/notes/ncert/extract-questions`, {
        text: ncertExtractText,
        exam_type: ncertExamType,
        subject: ncertSubject,
        class_level: ncertClass
      });
      if (res.data.success) {
        setExtractedQuestions(res.data.questions || []);
        toast.success(`Extracted ${res.data.count} questions. Review and confirm.`);
      } else {
        toast.error(res.data.error || "Extraction failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Extraction failed");
    } finally {
      setExtractingNCERT(false);
    }
  };
  
  // Add extracted questions to database
  const addExtractedQuestions = async () => {
    if (extractedQuestions.length === 0) return;
    try {
      const res = await axios.post(`${API}/notes/ncert/questions/add`, {
        exam_type: ncertExamType,
        subject: ncertSubject,
        class_level: ncertClass,
        questions: extractedQuestions
      });
      toast.success(res.data.message || "Questions added!");
      setShowNCERTExtractDialog(false);
      setExtractedQuestions([]);
      setNcertExtractText('');
      fetchNcertQuestions();
      fetchNcertStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add questions");
    }
  };
  
  // Fetch questions from AI
  const fetchQuestionsFromAI = async () => {
    if (!ncertExamType || !ncertSubject) {
      toast.error("Please select exam type and subject");
      return;
    }
    setFetchingNCERT(true);
    try {
      // If no class selected (All Classes), generate for all classes
      const classesToGenerate = ncertClass ? [ncertClass] : ncertClasses;
      const totalClasses = classesToGenerate.length;
      
      if (totalClasses > 1) {
        toast.info(`Starting bulk generation for ${totalClasses} classes. This may take several minutes...`);
      }
      
      const res = await axios.post(`${API}/notes/ncert/ai-generate-bulk`, {
        exam_type: ncertExamType,
        subject: ncertSubject,
        class_levels: classesToGenerate
      });
      
      toast.success(res.data.message || `Started generating questions for ${totalClasses} class(es)...`);
      
      // Poll for updates every 15 seconds for multi-class generation
      const pollInterval = totalClasses > 1 ? 15000 : 10000;
      setTimeout(() => {
        fetchNcertQuestions();
        fetchNcertStats();
      }, pollInterval);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate questions");
    } finally {
      setFetchingNCERT(false);
    }
  };
  
  // Delete NCERT question
  const deleteNcertQuestion = async (questionId) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await axios.delete(`${API}/notes/ncert/questions/${questionId}`);
      toast.success("Question deleted");
      fetchNcertQuestions();
      fetchNcertStats();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };
  
  // Update NCERT data when filters change
  useEffect(() => {
    if (ncertExamType) {
      fetchNcertClasses(ncertExamType);
      fetchNcertSubjects(ncertExamType);
    }
  }, [ncertExamType, fetchNcertClasses, fetchNcertSubjects]);
  
  useEffect(() => {
    if (ncertExamType && ncertSubject) {
      fetchNcertQuestions();
      fetchNcertStats();
    }
  }, [ncertExamType, ncertSubject, ncertClass, fetchNcertQuestions, fetchNcertStats]);

  // Poll for remote batch status updates
  useEffect(() => {
    if (isAuthenticated && activeTab === "batch") {
      const checkRemoteStatus = async () => {
        try {
          await axios.get(`${API}/notes/batch-jobs/check-remote-status`);
        } catch (e) {
          console.error("Remote status check failed", e);
        }
      };
      const interval = setInterval(checkRemoteStatus, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, activeTab]);

  // When exam changes in batch form, fetch its subjects
  useEffect(() => {
    if (batchJobForm.exam_type) {
      fetchBatchSubjects(batchJobForm.exam_type);
    } else {
      setBatchSubjectOptions([]);
    }
  }, [batchJobForm.exam_type, fetchBatchSubjects]);

  // Fetch batch jobs list and summary
  const fetchBatchJobs = useCallback(async () => {
    try {
      const [jobsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/notes/batch-jobs/list?limit=50`),
        axios.get(`${API}/notes/batch-jobs/stats/summary`)
      ]);
      setBatchJobs(jobsRes.data.jobs || []);
      setBatchJobsSummary(summaryRes.data);
    } catch (err) {
      console.error("Failed to fetch batch jobs:", err);
    }
  }, []);

  // Poll for active batch job progress
  useEffect(() => {
    const hasActiveJobs = batchJobs.some(j => j.status === 'processing' || j.status === 'pending');
    
    if (hasActiveJobs && isAuthenticated && activeTab === "batch") {
      // Start polling
      if (!pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(fetchBatchJobs, 2000);
      }
    } else {
      // Stop polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [batchJobs, isAuthenticated, activeTab, fetchBatchJobs]);

  // Create new batch job (supports Fast mode with Vertex AI or Cost-Saver with Batch API)
  const createBatchJob = async () => {
    setCreatingBatchJob(true);
    try {
      const subjects = batchJobForm.subjects.length > 0 ? batchJobForm.subjects : null;
      
      // Choose endpoint based on mode
      const endpoint = batchJobForm.mode === 'fast' 
        ? `${API}/notes/batch-jobs/create-fast`  // Vertex AI - real-time progress
        : `${API}/notes/batch-jobs/create`;       // Gemini Batch API - 12-24 hours
      
      const res = await axios.post(endpoint, {
        exam_type: batchJobForm.exam_type || null,
        subjects: subjects,
        language: batchJobForm.language
      });
      
      const modeText = batchJobForm.mode === 'fast' 
        ? 'Fast mode started with real-time progress!' 
        : 'Cost-saver mode started. Results in 12-24 hours.';
      
      toast.success(`Batch Job Started! ${res.data.total_topics} topics. ${modeText}`);
      setShowBatchJobDialog(false);
      setBatchJobForm({ exam_type: '', subjects: [], language: 'english', mode: 'fast' });
      setBatchSubjectOptions([]);
      fetchBatchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create batch job");
    } finally {
      setCreatingBatchJob(false);
    }
  };

  // Toggle subject selection in batch form
  const toggleBatchSubject = (subjectId) => {
    setBatchJobForm(prev => {
      const subjects = prev.subjects.includes(subjectId)
        ? prev.subjects.filter(s => s !== subjectId)
        : [...prev.subjects, subjectId];
      return { ...prev, subjects };
    });
  };

  // Select all subjects
  const selectAllBatchSubjects = () => {
    setBatchJobForm(prev => ({
      ...prev,
      subjects: batchSubjectOptions.map(s => s.id)
    }));
  };

  // Clear all subjects
  const clearAllBatchSubjects = () => {
    setBatchJobForm(prev => ({ ...prev, subjects: [] }));
  };

  // Cancel batch job
  const cancelBatchJob = async (jobId) => {
    try {
      await axios.post(`${API}/notes/batch-jobs/${jobId}/cancel`);
      toast.success("Batch job cancelled");
      fetchBatchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel job");
    }
  };


  // Resume batch job
  const resumeBatchJob = async (jobId) => {
    try {
      await axios.post(`${API}/notes/batch-jobs/${jobId}/resume`);
      toast.success("Batch job resumed");
      fetchBatchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resume job");
    }
  };

  // PDF Cache stats
  const fetchPdfStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notes/pdf/cache-stats`);
      setPdfStats(res.data);
    } catch (err) { /* silent */ }
  }, []);

  // Start bulk PDF generation for a specific exam or all
  const startBulkPdfGeneration = async (filters = {}) => {
    const key = filters.exam_type || "_all";
    setPdfGenerating(prev => ({ ...prev, [key]: true }));
    setPdfPaused(prev => ({ ...prev, [key]: false }));
    try {
      const res = await axios.post(`${API}/notes/pdf/bulk-generate`, filters);
      if (res.data.job_id) {
        setPdfJobId(prev => ({ ...prev, [key]: res.data.job_id }));
        toast.success(res.data.message);
      } else {
        toast.info(res.data.message);
        fetchPdfStats();
      }
    } catch (err) {
      toast.error("Failed to start PDF generation");
    } finally {
      setPdfGenerating(prev => ({ ...prev, [key]: false }));
    }
  };

  // Pause bulk PDF generation
  const pauseBulkPdfGeneration = async (key) => {
    const jobId = pdfJobId[key];
    if (!jobId) return;
    try {
      await axios.post(`${API}/notes/pdf/bulk-pause/${jobId}`);
      setPdfPaused(prev => ({ ...prev, [key]: true }));
      toast.info("PDF generation paused");
    } catch (err) {
      toast.error("Failed to pause");
    }
  };

  // Resume bulk PDF generation
  const resumeBulkPdfGeneration = async (key) => {
    const jobId = pdfJobId[key];
    if (!jobId) return;
    try {
      await axios.post(`${API}/notes/pdf/bulk-resume/${jobId}`);
      setPdfPaused(prev => ({ ...prev, [key]: false }));
      toast.success("PDF generation resumed");
    } catch (err) {
      toast.error("Failed to resume");
    }
  };

  // Poll PDF job status for all active jobs
  useEffect(() => {
    if (!pdfJobId || Object.keys(pdfJobId).length === 0) return;
    const interval = setInterval(async () => {
      let anyRunning = false;
      for (const [key, jobId] of Object.entries(pdfJobId)) {
        if (!jobId) continue;
        try {
          const res = await axios.get(`${API}/notes/pdf/bulk-status/${jobId}`);
          setPdfJobStatus(prev => ({ ...prev, [key]: res.data }));
          // Update paused state from server
          if (res.data.paused) {
            setPdfPaused(prev => ({ ...prev, [key]: true }));
          }
          if (res.data.status === "completed") {
            toast.success(`${key === "_all" ? "All" : key.toUpperCase()} PDFs done! ${res.data.completed} generated.`);
            setPdfJobId(prev => { const n = { ...prev }; delete n[key]; return n; });
            setPdfPaused(prev => { const n = { ...prev }; delete n[key]; return n; });
            fetchPdfStats();
          } else {
            anyRunning = true;
          }
        } catch { /* silent */ }
      }
      if (!anyRunning) clearInterval(interval);
    }, 1500);
    return () => clearInterval(interval);
  }, [pdfJobId, fetchPdfStats]);

  // Fetch stats and config on load
  useEffect(() => {
    if (isAuthenticated) {
      fetchPdfStats();
      fetchBatchJobs();
      fetchLLMConfig();
    }
  }, [isAuthenticated, fetchPdfStats, fetchBatchJobs, fetchLLMConfig]);

  // Handle login
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === LMS_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('lms_manager_authenticated', 'true');
      toast.success("Access granted");
    } else {
      toast.error("Invalid password");
    }
  };

  // Fetch exam categories
  const fetchExams = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notes/lms/syllabus/exams`);
      setExams(res.data.exams || []);
    } catch (err) {
      toast.error("Failed to load exam categories");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch subjects for selected exam
  const fetchSubjects = useCallback(async (examId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notes/lms/syllabus/exams/${examId}/subjects`);
      setSubjects(res.data.subjects || []);
    } catch (err) {
      toast.error("Failed to load subjects");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch topics for selected subject
  const fetchTopics = useCallback(async (examId, subjectId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notes/lms/syllabus/exams/${examId}/subjects/${subjectId}/topics`);
      setTopics(res.data.topics || []);
      setTopicIndexes(res.data.topic_indexes || {});
    } catch (err) {
      toast.error("Failed to load topics");
    } finally {
      setLoading(false);
    }
  }, []);

  // Save topic index
  const saveTopicIndex = async (topic) => {
    if (!selectedExam || !selectedSubject) return;
    setSavingTopicIndex(topic);
    try {
      await axios.put(`${API}/notes/lms/syllabus/exams/${selectedExam.id}/subjects/${selectedSubject.id}/topic-indexes`, {
        topic,
        index_text: topicIndexes[topic] || ""
      });
      toast.success("Topic index saved!");
    } catch (err) {
      toast.error("Failed to save topic index");
    } finally {
      setSavingTopicIndex(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchExams();
    }
  }, [isAuthenticated, fetchExams]);

  useEffect(() => {
    if (selectedExam) {
      fetchSubjects(selectedExam.id);
      setSelectedSubject(null);
      setTopics([]);
    }
  }, [selectedExam, fetchSubjects]);

  useEffect(() => {
    if (selectedExam && selectedSubject) {
      fetchTopics(selectedExam.id, selectedSubject.id);
    }
  }, [selectedExam, selectedSubject, fetchTopics]);

  // Add new exam
  const addExam = async () => {
    if (!newExamId.trim()) {
      toast.error("Exam ID is required");
      return;
    }
    try {
      await axios.post(`${API}/notes/lms/syllabus/exams`, {
        id: newExamId,
        name: newExamName || newExamId.toUpperCase()
      });
      toast.success("Exam category added!");
      setShowAddExam(false);
      setNewExamId("");
      setNewExamName("");
      fetchExams();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add exam");
    }
  };

  // Delete exam
  const deleteExam = async (examId) => {
    if (!window.confirm(`Delete exam category "${examId || '(blank)'}"? This will remove all subjects and topics!`)) return;
    try {
      if (!examId) {
        // Special case: delete blank exam_type entries
        await axios.delete(`${API}/notes/lms/syllabus/exams-blank`);
      } else {
        await axios.delete(`${API}/notes/lms/syllabus/exams/${examId}`);
      }
      toast.success("Exam category deleted!");
      if (selectedExam?.id === examId) {
        setSelectedExam(null);
        setSubjects([]);
      }
      fetchExams();
    } catch (err) {
      toast.error("Failed to delete exam");
    }
  };

  // Add new subject
  const addSubject = async () => {
    if (!newSubjectId.trim() || !selectedExam) {
      toast.error("Subject ID is required");
      return;
    }
    try {
      await axios.post(`${API}/notes/lms/syllabus/exams/${selectedExam.id}/subjects`, {
        id: newSubjectId,
        name: newSubjectName || newSubjectId.replace(/_/g, ' ')
      });
      toast.success("Subject added!");
      setShowAddSubject(false);
      setNewSubjectId("");
      setNewSubjectName("");
      fetchSubjects(selectedExam.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add subject");
    }
  };

  // Delete subject
  const deleteSubject = async (subjectId) => {
    if (!window.confirm(`Delete subject "${subjectId || '(blank)'}"? This will remove all topics!`)) return;
    try {
      // Use safe endpoint to handle blank IDs and IDs with slashes (/)
      await axios.delete(`${API}/notes/lms/syllabus/exams/${selectedExam.id}/subjects-safe`, {
        params: { subject_id: subjectId }
      });
      toast.success("Subject deleted!");
      if (selectedSubject?.id === subjectId) {
        setSelectedSubject(null);
        setTopics([]);
      }
      fetchSubjects(selectedExam.id);
    } catch (err) {
      toast.error("Failed to delete subject");
    }
  };

  // Rename exam category
  const renameExam = async () => {
    if (!newDisplayName.trim() || !renameTarget) {
      toast.error("New name is required");
      return;
    }
    try {
      await axios.put(`${API}/notes/lms/syllabus/exams/${renameTarget.id}/rename`, {
        name: newDisplayName
      });
      toast.success("Exam renamed successfully!");
      setShowRenameExam(false);
      setRenameTarget(null);
      setNewDisplayName("");
      fetchExams();
    } catch (err) {
      toast.error("Failed to rename exam");
    }
  };

  // Rename subject
  const renameSubject = async () => {
    if (!newDisplayName.trim() || !renameTarget || !selectedExam) {
      toast.error("New name is required");
      return;
    }
    try {
      // Use safe endpoint to handle IDs with slashes
      await axios.put(`${API}/notes/lms/syllabus/exams/${selectedExam.id}/subjects-rename-safe`, 
        { name: newDisplayName },
        { params: { subject_id: renameTarget.id } }
      );
      toast.success("Subject renamed successfully!");
      setShowRenameSubject(false);
      setRenameTarget(null);
      setNewDisplayName("");
      fetchSubjects(selectedExam.id);
    } catch (err) {
      toast.error("Failed to rename subject");
    }
  };

  // Import topics from text
  const importTopics = async () => {
    if (!topicsText.trim() || !selectedExam || !selectedSubject) {
      toast.error("Topics text is required");
      return;
    }
    try {
      const res = await axios.post(`${API}/notes/lms/syllabus/import-topics`, {
        exam_id: selectedExam.id,
        subject_id: selectedSubject.id,
        text: topicsText
      });
      toast.success(`Added ${res.data.count} topics!`);
      setShowAddTopics(false);
      setTopicsText("");
      fetchTopics(selectedExam.id, selectedSubject.id);
    } catch (err) {
      toast.error("Failed to import topics");
    }
  };

  // Generate topics with AI
  const generateTopicsAI = async () => {
    if (!selectedExam || !selectedSubject) return;
    
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/notes/lms/syllabus/generate-topics`, {
        exam_id: selectedExam.id,
        subject_id: selectedSubject.id,
        description: aiDescription,
        count: aiTopicCount
      });
      setGeneratedTopics(res.data.topics || []);
      toast.success(`Generated ${res.data.count} topics!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate topics");
    } finally {
      setGenerating(false);
    }
  };

  // Save generated topics
  const saveGeneratedTopics = async () => {
    if (!generatedTopics.length) return;
    try {
      const res = await axios.post(`${API}/notes/lms/syllabus/exams/${selectedExam.id}/subjects/${selectedSubject.id}/topics`, {
        topics: generatedTopics
      });
      toast.success(`Added ${res.data.count} topics!`);
      setShowGenerateTopics(false);
      setGeneratedTopics([]);
      setAiDescription("");
      fetchTopics(selectedExam.id, selectedSubject.id);
    } catch (err) {
      toast.error("Failed to save topics");
    }
  };

  // Extract topics with AI (from text or image)
  const extractTopicsAI = async () => {
    if (!extractText && !extractImage) {
      toast.error("Please provide text or upload an image");
      return;
    }
    if (!selectedExam || !selectedSubject) {
      toast.error("Please select exam and subject first");
      return;
    }
    
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('exam_id', selectedExam.id);
      formData.append('subject_id', selectedSubject.id);
      formData.append('auto_add', 'true');
      
      if (extractText) {
        formData.append('text_input', extractText);
      }
      if (extractImage) {
        formData.append('image', extractImage);
      }
      
      const res = await axios.post(`${API}/notes/lms/syllabus/extract-topics-ai`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setExtractedTopics(res.data.extracted_topics || []);
      toast.success(`Extracted ${res.data.total_extracted} topics, added ${res.data.total_added} new topics!`);
      
      // Refresh topics list
      fetchTopics(selectedExam.id, selectedSubject.id);
      
    } catch (err) {
      console.error("Extract error:", err);
      toast.error(err.response?.data?.detail || "Failed to extract topics");
    } finally {
      setExtracting(false);
    }
  };

  // Clear extract form
  const clearExtractForm = () => {
    setExtractText("");
    setExtractImage(null);
    setExtractedTopics([]);
    setShowExtractTopics(false);
  };

  // Delete topic
  const deleteTopic = async (topic) => {
    try {
      await axios.delete(`${API}/notes/lms/syllabus/exams/${selectedExam.id}/subjects/${selectedSubject.id}/topics?topic=${encodeURIComponent(topic)}`);
      toast.success("Topic deleted!");
      fetchTopics(selectedExam.id, selectedSubject.id);
    } catch (err) {
      toast.error("Failed to delete topic");
    }
  };

  // Clear all topics
  const clearAllTopics = async () => {
    if (!window.confirm(`Clear ALL ${topics.length} topics? This cannot be undone!`)) return;
    try {
      await axios.delete(`${API}/notes/lms/syllabus/exams/${selectedExam.id}/subjects/${selectedSubject.id}/topics/all`);
      toast.success("All topics cleared!");
      fetchTopics(selectedExam.id, selectedSubject.id);
    } catch (err) {
      toast.error("Failed to clear topics");
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-xl border-white/20">
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Settings className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">LMS Manager</h1>
              <p className="text-gray-400 text-sm">Syllabus Management Portal</p>
              <Badge className="mt-2 bg-indigo-600/30 text-indigo-300 border-indigo-500/50">Owner Access</Badge>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                data-testid="lms-manager-password"
              />
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="lms-manager-login">
                <Lock className="w-4 h-4 mr-2" />
                Access Portal
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-gray-400 hover:text-white">Back to Home</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
      {/* Header */}
      <nav className="bg-black/30 border-b border-white/10 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-indigo-400" />
            <span className="font-bold text-white text-lg">LMS Manager</span>
            <Badge className="bg-indigo-600/30 text-indigo-300 border-indigo-500/50 text-xs">Syllabus Portal</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/lms">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                <BookOpen className="w-4 h-4 mr-2" />
                Notes Manager
              </Button>
            </Link>
            <Link to="/admin">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                Admin
              </Button>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                <Home className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Main Tabs: Syllabus Management vs Batch Jobs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-6xl grid-cols-10 bg-white/10 mb-6">
            <TabsTrigger value="syllabus" className="data-[state=active]:bg-indigo-600 text-white">
              <Layers className="w-4 h-4 mr-2" />
              Syllabus
            </TabsTrigger>
            <TabsTrigger value="batch" className="data-[state=active]:bg-indigo-600 text-white relative">
              <Zap className="w-4 h-4 mr-2" />
              Batch Jobs
              {batchJobsSummary?.running_count > 0 && (
                <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {batchJobsSummary.running_count}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="ncert" className="data-[state=active]:bg-orange-600 text-white">
              <BookMarked className="w-4 h-4 mr-2" />
              NCERT
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-pink-600 text-white">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="team" className="data-[state=active]:bg-green-600 text-white">
              <Users className="w-4 h-4 mr-2" />
              Team
            </TabsTrigger>
            <TabsTrigger value="chats" className="data-[state=active]:bg-red-600 text-white">
              <MessageCircle className="w-4 h-4 mr-2" />
              Chats
            </TabsTrigger>
            <TabsTrigger value="cache" className="data-[state=active]:bg-red-600 text-white">
              <Database className="w-4 h-4 mr-2" />
              Cache
            </TabsTrigger>
            <TabsTrigger value="question-bank" className="data-[state=active]:bg-emerald-600 text-white">
              <BookOpen className="w-4 h-4 mr-2" />
              Question Bank
            </TabsTrigger>
            <TabsTrigger value="payments-sync" className="data-[state=active]:bg-orange-600 text-white">
              <DollarSign className="w-4 h-4 mr-2" />
              Pay Sync
            </TabsTrigger>
            <TabsTrigger value="mock-tests" className="data-[state=active]:bg-rose-600 text-white">
              <Clock className="w-4 h-4 mr-2" />
              Mock Tests
            </TabsTrigger>
          </TabsList>

          {/* ===== BATCH JOBS TAB ===== */}
          <TabsContent value="batch" className="mt-0">
            <div className="space-y-6">
              {/* Header with Create Button */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Zap className="w-6 h-6 text-amber-400" />
                    Batch API Jobs
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Bulk note generation using Gemini 3 Flash Batch API (50% discount)
                  </p>
                </div>
                <Dialog open={showBatchJobDialog} onOpenChange={setShowBatchJobDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-600 hover:bg-amber-700" data-testid="create-batch-job-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Batch Job
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-white/20 text-white">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-amber-400" />
                        Create New Batch Job
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">Exam Type (optional - leave empty for all)</label>
                        <select
                          value={batchJobForm.exam_type}
                          onChange={(e) => setBatchJobForm(f => ({ ...f, exam_type: e.target.value }))}
                          className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2"
                        >
                          <option value="">All Exams</option>
                          {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      
                      {/* Subjects Multi-Select */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-sm text-gray-400">Subjects {batchJobForm.exam_type ? '' : '(select exam first)'}</label>
                          {batchSubjectOptions.length > 0 && (
                            <div className="flex gap-2">
                              <button 
                                type="button"
                                onClick={selectAllBatchSubjects}
                                className="text-xs text-indigo-400 hover:text-indigo-300"
                              >
                                Select All
                              </button>
                              <span className="text-gray-600">|</span>
                              <button 
                                type="button"
                                onClick={clearAllBatchSubjects}
                                className="text-xs text-gray-400 hover:text-gray-300"
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {!batchJobForm.exam_type ? (
                          <div className="bg-white/5 border border-white/10 rounded-md p-3 text-center text-gray-500 text-sm">
                            Select an exam to see available subjects
                          </div>
                        ) : batchSubjectOptions.length === 0 ? (
                          <div className="bg-white/5 border border-white/10 rounded-md p-3 text-center text-gray-500 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                            Loading subjects...
                          </div>
                        ) : (
                          <div className="bg-white/5 border border-white/10 rounded-md p-2 max-h-48 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-2">
                              {batchSubjectOptions.map(subject => (
                                <label 
                                  key={subject.id}
                                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                    batchJobForm.subjects.includes(subject.id) 
                                      ? 'bg-indigo-600/30 border border-indigo-500/50' 
                                      : 'hover:bg-white/10 border border-transparent'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={batchJobForm.subjects.includes(subject.id)}
                                    onChange={() => toggleBatchSubject(subject.id)}
                                    className="w-4 h-4 rounded border-white/30 bg-white/10 text-indigo-600 focus:ring-indigo-500"
                                  />
                                  <span className="text-white text-sm truncate">{subject.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                        {batchJobForm.subjects.length > 0 && (
                          <div className="mt-1 text-xs text-indigo-400">
                            {batchJobForm.subjects.length} subject{batchJobForm.subjects.length > 1 ? 's' : ''} selected
                          </div>
                        )}
                      </div>
                      
                      {/* Language Selection */}
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">Language</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setBatchJobForm(f => ({ ...f, language: 'english' }))}
                            className={`flex-1 py-2 px-3 rounded-md border transition-colors ${
                              batchJobForm.language === 'english'
                                ? 'bg-indigo-600 border-indigo-500 text-white'
                                : 'bg-white/5 border-white/20 text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            English
                          </button>
                          <button
                            type="button"
                            onClick={() => setBatchJobForm(f => ({ ...f, language: 'hindi' }))}
                            className={`flex-1 py-2 px-3 rounded-md border transition-colors ${
                              batchJobForm.language === 'hindi'
                                ? 'bg-indigo-600 border-indigo-500 text-white'
                                : 'bg-white/5 border-white/20 text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            Hindi
                          </button>
                          <button
                            type="button"
                            onClick={() => setBatchJobForm(f => ({ ...f, language: 'both' }))}
                            className={`flex-1 py-2 px-3 rounded-md border transition-colors ${
                              batchJobForm.language === 'both'
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 border-indigo-500 text-white'
                                : 'bg-white/5 border-white/20 text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            Both
                          </button>
                        </div>
                        {batchJobForm.language === 'both' && (
                          <div className="mt-1 text-xs text-purple-400">
                            Will generate notes in both English & Hindi
                          </div>
                        )}
                      </div>
                      
                      {/* Mode Selection - Fast vs Cost-Saver */}
                      <div>
                        <label className="text-sm text-gray-400 mb-2 block">Processing Mode</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setBatchJobForm(f => ({ ...f, mode: 'fast' }))}
                            className={`p-3 rounded-lg border transition-all ${
                              batchJobForm.mode === 'fast'
                                ? 'bg-green-600/20 border-green-500 text-green-300'
                                : 'bg-white/5 border-white/20 text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            <Zap className="w-5 h-5 mx-auto mb-1" />
                            <div className="font-medium text-sm">Fast Mode</div>
                            <div className="text-xs opacity-70 mt-1">Vertex AI • Real-time</div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setBatchJobForm(f => ({ ...f, mode: 'cost_saver' }))}
                            className={`p-3 rounded-lg border transition-all ${
                              batchJobForm.mode === 'cost_saver'
                                ? 'bg-amber-600/20 border-amber-500 text-amber-300'
                                : 'bg-white/5 border-white/20 text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            <Clock className="w-5 h-5 mx-auto mb-1" />
                            <div className="font-medium text-sm">Cost-Saver</div>
                            <div className="text-xs opacity-70 mt-1">50% off • 12-24 hrs</div>
                          </button>
                        </div>
                      </div>
                      
                      {/* Mode Info Box */}
                      {batchJobForm.mode === 'fast' ? (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm">
                          <div className="flex items-start gap-2">
                            <Zap className="w-4 h-4 text-green-400 mt-0.5" />
                            <div>
                              <p className="text-green-300 font-medium">Vertex AI Fast Processing</p>
                              <p className="text-gray-400 text-xs mt-1">
                                High-throughput processing (30,000 RPM).<br/>
                                Real-time progress tracking. Notes & PDFs generated immediately.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm">
                          <div className="flex items-start gap-2">
                            <Clock className="w-4 h-4 text-amber-400 mt-0.5" />
                            <div>
                              <p className="text-amber-300 font-medium">Gemini Batch API (50% Discount)</p>
                              <p className="text-gray-400 text-xs mt-1">
                                Cost-effective batch processing.<br/>
                                Processing time: 12-24 hours. Email notification on completion.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <Button 
                        onClick={createBatchJob} 
                        disabled={creatingBatchJob}
                        className="w-full bg-amber-600 hover:bg-amber-700"
                        data-testid="start-batch-job-btn"
                      >
                        {creatingBatchJob ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                        ) : (
                          <><Zap className="w-4 h-4 mr-2" />Start Batch Job</>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Summary Stats */}
              {batchJobsSummary && (
                <div className="grid grid-cols-4 gap-4">
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="py-4 text-center">
                      <Activity className="w-6 h-6 text-green-400 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-green-400">{batchJobsSummary.running_count}</div>
                      <div className="text-xs text-gray-400">Running</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="py-4 text-center">
                      <FileText className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-blue-400">{batchJobsSummary.total_notes_generated}</div>
                      <div className="text-xs text-gray-400">Notes Generated</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="py-4 text-center">
                      <DollarSign className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-amber-400">${batchJobsSummary.total_cost_usd?.toFixed(2) || '0.00'}</div>
                      <div className="text-xs text-gray-400">Total Cost</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="py-4 text-center">
                      <BarChart3 className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-purple-400">
                        {Object.values(batchJobsSummary.status_counts || {}).reduce((a, b) => a + b, 0)}
                      </div>
                      <div className="text-xs text-gray-400">Total Jobs</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Active Jobs */}
              {batchJobsSummary?.running_jobs?.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-green-400" />
                    Active Jobs
                  </h3>
                  {batchJobsSummary.running_jobs.map(job => (
                    <Card key={job.job_id} className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border-green-500/30">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                              <Loader2 className="w-5 h-5 text-green-400 animate-spin" />
                            </div>
                            <div>
                              <div className="text-white font-medium">
                                {job.provider === 'gemini_batch_api' ? 'Gemini Batch API' : job.provider}
                              </div>
                              <div className="text-xs text-gray-400">
                                Started {new Date(job.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <div className="text-lg font-bold text-white">
                                {job.completed}/{job.total_topics}
                              </div>
                              <div className="text-xs text-gray-400">
                                {job.pdfs_generated > 0 && <span className="text-blue-400 mr-2">{job.pdfs_generated} PDFs</span>}
                                {job.failed > 0 && <span className="text-red-400">{job.failed} failed</span>}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {/* Resume Button - show for stalled jobs (not updated in 2+ minutes) */}
                              {job.mode === 'fast' && job.status === 'processing' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => resumeBatchJob(job.job_id)}
                                  className="bg-green-600/20 hover:bg-green-600/40 text-green-400 border-green-500/30"
                                  title="Resume stalled job"
                                >
                                  <Play className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => cancelBatchJob(job.job_id)}
                                className="bg-red-600/20 hover:bg-red-600/40 text-red-400"
                              >
                                <StopCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Current Topic */}
                        {job.current_topic && (
                          <div className="bg-black/30 rounded-lg p-2 mb-3">
                            <div className="text-xs text-gray-400 mb-1">Currently processing:</div>
                            <div className="text-white text-sm truncate">{job.current_topic}</div>
                          </div>
                        )}
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-white/10 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-emerald-400 h-2 rounded-full transition-all"
                            style={{ width: `${job.progress_percent || 0}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          <span>{job.progress_percent?.toFixed(1) || 0}% complete</span>
                          {job.estimated_cost_usd && (
                            <span className="text-amber-400">Est: ${job.estimated_cost_usd}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* All Jobs History */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold">Job History</h3>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={fetchBatchJobs}
                    className="text-gray-400 hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Refresh
                  </Button>
                </div>
                
                {batchJobs.length === 0 ? (
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="py-12 text-center">
                      <Zap className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                      <p className="text-gray-400">No batch jobs yet. Create your first batch job!</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {batchJobs.filter(j => j.status !== 'processing' && j.status !== 'pending').map(job => (
                      <Card key={job.job_id} className={`border-white/10 ${
                        job.status === 'completed' ? 'bg-green-900/20' :
                        job.status === 'failed' ? 'bg-red-900/20' :
                        job.status === 'cancelled' ? 'bg-gray-900/20' : 'bg-white/5'
                      }`}>
                        <CardContent className="py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {job.status === 'completed' ? (
                                <CheckCircle className="w-5 h-5 text-green-400" />
                              ) : job.status === 'failed' ? (
                                <XCircle className="w-5 h-5 text-red-400" />
                              ) : (
                                <StopCircle className="w-5 h-5 text-gray-400" />
                              )}
                              <div>
                                <div className="text-white text-sm">
                                  {job.completed}/{job.total_topics} notes • {job.pdfs_generated || job.completed} PDFs • {job.provider}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {new Date(job.created_at).toLocaleString()}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {job.actual_cost_usd && (
                                <span className="text-amber-400 text-sm">${job.actual_cost_usd}</span>
                              )}
                              <Badge className={`${
                                job.status === 'completed' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                job.status === 'failed' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              }`}>
                                {job.status}
                              </Badge>
                              {job.status !== 'completed' && job.status !== 'cancelled' && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => resumeBatchJob(job.job_id)}
                                  className="h-7 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                                >
                                  <Play className="w-3 h-3 mr-1" /> Resume
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ===== SYLLABUS TAB ===== */}
          <TabsContent value="syllabus" className="mt-0">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <span 
            className={`cursor-pointer hover:text-white ${!selectedExam ? 'text-indigo-400' : ''}`}
            onClick={() => { setSelectedExam(null); setSelectedSubject(null); setTopics([]); }}
          >
            Exams
          </span>
          {selectedExam && (
            <>
              <ChevronRight className="w-4 h-4" />
              <span 
                className={`cursor-pointer hover:text-white ${!selectedSubject ? 'text-indigo-400' : ''}`}
                onClick={() => { setSelectedSubject(null); setTopics([]); }}
              >
                {selectedExam.name}
              </span>
            </>
          )}
          {selectedSubject && (
            <>
              <ChevronRight className="w-4 h-4" />
              <span className="text-indigo-400">{selectedSubject.name}</span>
            </>
          )}
        </div>

        {/* Level 1: Exam Categories */}
        {!selectedExam && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Layers className="w-6 h-6 text-indigo-400" />
                Exam Categories
              </h2>
              <Dialog open={showAddExam} onOpenChange={setShowAddExam}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-exam-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Exam Category
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-white/20 text-white">
                  <DialogHeader>
                    <DialogTitle>Add New Exam Category</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Exam ID (e.g., ctet, tet)</label>
                      <Input
                        value={newExamId}
                        onChange={(e) => setNewExamId(e.target.value.toLowerCase().replace(/\s/g, '_'))}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="exam_id"
                        data-testid="new-exam-id"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Display Name (optional)</label>
                      <Input
                        value={newExamName}
                        onChange={(e) => setNewExamName(e.target.value)}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="CTET"
                      />
                    </div>
                    <Button onClick={addExam} className="w-full bg-indigo-600 hover:bg-indigo-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Exam Category
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {exams.map((exam) => {
                  const examPdf = pdfStats?.by_exam?.find(e => e.exam_type === exam.id);
                  const totalNotes = examPdf?.total_notes || 0;
                  const cachedPdfs = examPdf?.cached_pdfs || 0;
                  const uncached = examPdf?.uncached || 0;
                  const totalTopics = exam.topics_count || 0;
                  const notesNotCreated = totalTopics - totalNotes;
                  const mcqCount = examPdf?.mcq_count || 0;
                  const mcqTopics = examPdf?.mcq_topics || 0;
                  const mcqTopicsRemaining = examPdf?.mcq_topics_remaining || 0;
                  const key = exam.id;
                  const isRunning = pdfJobStatus[key]?.status === "running";
                  const allPdfsDone = totalNotes > 0 && uncached === 0;
                  const progress = isRunning ? Math.round((pdfJobStatus[key].completed / Math.max(1, pdfJobStatus[key].total)) * 100) : 0;

                  return (
                  <Card 
                    key={exam.id} 
                    className="bg-white/5 border-white/10 hover:bg-white/10 cursor-pointer transition-all group"
                    onClick={() => setSelectedExam(exam)}
                    data-testid={`exam-card-${exam.id}`}
                  >
                    <CardContent className="py-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-indigo-600/30 rounded-xl flex items-center justify-center">
                          <BookMarked className="w-6 h-6 text-indigo-400" />
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setRenameTarget(exam);
                              setNewDisplayName(exam.name);
                              setShowRenameExam(true);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={(e) => { e.stopPropagation(); deleteExam(exam.id); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{exam.name}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                        <span>{exam.subjects_count} Subjects</span>
                        <span>{totalTopics} Topics</span>
                      </div>

                      {/* PDF Status Section */}
                      <div className="border-t border-white/10 pt-3 mt-1" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-gray-400 font-medium">PDF Cache</span>
                          <span className="font-mono">
                            <span className="text-green-400">{cachedPdfs}</span>
                            <span className="text-gray-500"> / </span>
                            <span className="text-white">{totalNotes}</span>
                            <span className="text-gray-500"> notes</span>
                            {notesNotCreated > 0 && (
                              <span className="text-gray-600 ml-1">({notesNotCreated} notes pending)</span>
                            )}
                          </span>
                        </div>

                        {/* Progress bar */}
                        <div className="w-full bg-white/10 rounded-full h-1.5 mb-2">
                          <div
                            className={`h-1.5 rounded-full transition-all ${allPdfsDone ? 'bg-green-500' : totalNotes === 0 ? 'bg-gray-600' : 'bg-amber-500'}`}
                            style={{ width: totalNotes === 0 ? '0%' : `${(cachedPdfs / Math.max(1, totalNotes)) * 100}%` }}
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs">
                            {totalNotes === 0 ? (
                              <span className="text-gray-500">No notes created yet</span>
                            ) : allPdfsDone ? (
                              <span className="text-green-400">All {cachedPdfs} PDFs cached ({examPdf?.size_mb || 0} MB)</span>
                            ) : (
                              <span className="text-amber-400">{uncached} PDFs remaining</span>
                            )}
                          </span>
                          {totalNotes > 0 && uncached > 0 && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); startBulkPdfGeneration({ exam_type: key }); }}
                              disabled={pdfGenerating[key] || !!pdfJobId[key]}
                              className="h-6 px-2.5 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                              data-testid={`bulk-pdf-btn-${key}`}
                            >
                              {pdfJobId[key] ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" />{progress}%</>
                              ) : (
                                <><FileDown className="w-3 h-3 mr-1" />Generate {uncached} PDFs</>
                              )}
                            </Button>
                          )}
                          {allPdfsDone && (
                            <span className="flex items-center text-xs text-green-400">
                              <CheckCircle className="w-3 h-3 mr-1" />Done
                            </span>
                          )}
                        </div>

                        {/* Running progress detail */}
                        {isRunning && (
                          <div className="mt-2 text-xs text-gray-400 truncate" data-testid={`pdf-progress-${key}`}>
                            <span className="text-amber-400">{pdfJobStatus[key].completed}/{pdfJobStatus[key].total}</span>
                            <span className="ml-1.5">{pdfJobStatus[key].current_topic}</span>
                          </div>
                        )}

                        {/* MCQ Stats */}
                        <div className="border-t border-white/10 pt-2.5 mt-2.5">
                          <div className="flex items-center justify-between text-xs mb-1.5">
                            <span className="text-gray-400 font-medium flex items-center gap-1">
                              <CircleHelp className="w-3 h-3" />MCQs
                            </span>
                            <span className="font-mono">
                              <span className={mcqTopics > 0 ? "text-blue-400" : "text-gray-500"}>{mcqCount}</span>
                              <span className="text-gray-500"> questions</span>
                              <span className="text-gray-600 ml-1">({mcqTopics}/{totalTopics} topics)</span>
                            </span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all ${mcqTopics >= totalTopics && totalTopics > 0 ? 'bg-blue-500' : mcqTopics > 0 ? 'bg-blue-400/60' : 'bg-gray-600'}`}
                              style={{ width: totalTopics === 0 ? '0%' : `${(mcqTopics / Math.max(1, totalTopics)) * 100}%` }}
                            />
                          </div>
                          <div className="text-xs mt-1">
                            {totalTopics === 0 ? (
                              <span className="text-gray-500">No topics configured</span>
                            ) : mcqTopicsRemaining <= 0 ? (
                              <span className="text-blue-400">All topics have MCQs</span>
                            ) : (
                              <span className="text-gray-500">{mcqTopicsRemaining} topics without MCQs</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}

            {/* PDF Overall Summary */}
            {pdfStats && (
              <div className="mt-8" data-testid="pdf-bulk-section">
                <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-amber-500/30 shadow-xl">
                  <CardContent className="py-5 px-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-500/20 rounded-lg">
                          <FileDown className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-lg">Bulk PDF Generation</h3>
                          <p className="text-gray-400 text-xs">Generate PDFs for all notes - available on production site</p>
                        </div>
                      </div>
                      
                      {/* Start/Controls */}
                      <div className="flex items-center gap-2">
                        {pdfJobId["_all"] ? (
                          <>
                            {pdfPaused["_all"] ? (
                              <Button
                                size="sm"
                                onClick={() => resumeBulkPdfGeneration("_all")}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid="bulk-pdf-resume-btn"
                              >
                                <Play className="w-4 h-4 mr-1" /> Resume
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => pauseBulkPdfGeneration("_all")}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="bulk-pdf-pause-btn"
                              >
                                <Pause className="w-4 h-4 mr-1" /> Pause
                              </Button>
                            )}
                          </>
                        ) : (
                          <Button
                            onClick={() => startBulkPdfGeneration({})}
                            disabled={pdfGenerating["_all"] || pdfStats.uncached === 0}
                            className={`${pdfStats.uncached === 0 ? 'bg-green-600' : 'bg-indigo-600 hover:bg-indigo-700'} text-white font-semibold px-6`}
                            data-testid="bulk-pdf-generate-all-btn"
                          >
                            {pdfGenerating["_all"] ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting...</>
                            ) : pdfStats.uncached === 0 ? (
                              <><CheckCircle className="w-4 h-4 mr-2" />All PDFs Cached</>
                            ) : (
                              <><FileDown className="w-4 h-4 mr-2" />Start Bulk Generation ({pdfStats.uncached})</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Stats Row */}
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-400">{pdfStats.cached_pdfs}</div>
                        <div className="text-xs text-gray-400">Cached PDFs</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-white">{pdfStats.total_notes}</div>
                        <div className="text-xs text-gray-400">Total Notes</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-amber-400">{pdfStats.uncached}</div>
                        <div className="text-xs text-gray-400">Remaining</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-blue-400">{pdfStats.total_size_mb} MB</div>
                        <div className="text-xs text-gray-400">Cache Size</div>
                      </div>
                    </div>
                    
                    {/* Overall Progress Bar */}
                    <div className="mb-2">
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>Overall Cache Progress</span>
                        <span className="font-mono">{pdfStats.cache_coverage}</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-400 h-2 rounded-full transition-all" style={{ width: pdfStats.cache_coverage }} />
                      </div>
                    </div>
                    
                    {/* Active Job Progress */}
                    {pdfJobStatus["_all"] && (pdfJobStatus["_all"].status === "running" || pdfJobStatus["_all"].status === "paused" || pdfJobStatus["_all"].status?.startsWith("retrying")) && (
                      <div className="mt-4 bg-gradient-to-br from-indigo-900/50 to-purple-900/50 rounded-xl p-4 border border-indigo-500/30">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {pdfJobStatus["_all"].status === "paused" ? (
                              <Pause className="w-5 h-5 text-amber-400" />
                            ) : pdfJobStatus["_all"].status?.startsWith("retrying") ? (
                              <AlertCircle className="w-5 h-5 text-amber-400 animate-pulse" />
                            ) : (
                              <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
                            )}
                            <span className="text-white font-semibold">
                              {pdfJobStatus["_all"].status === "paused" ? "Paused" : 
                               pdfJobStatus["_all"].status?.startsWith("retrying") ? "Retrying..." : "Generating PDFs..."}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-400">
                              {pdfJobStatus["_all"].errors > 0 && (
                                <span className="text-red-400 mr-2">{pdfJobStatus["_all"].errors} errors</span>
                              )}
                              {pdfJobStatus["_all"].skipped > 0 && (
                                <span className="text-gray-500">{pdfJobStatus["_all"].skipped} skipped</span>
                              )}
                            </span>
                            <span className="font-mono text-lg font-bold text-indigo-300">
                              {pdfJobStatus["_all"].completed}/{pdfJobStatus["_all"].total}
                            </span>
                          </div>
                        </div>
                        
                        {/* Current Topic Info */}
                        <div className="bg-black/30 rounded-lg p-3 mb-3">
                          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                            <span className="bg-indigo-500/30 px-2 py-0.5 rounded text-indigo-300">{pdfJobStatus["_all"].current_exam}</span>
                            <span className="text-gray-500">→</span>
                            <span className="bg-purple-500/30 px-2 py-0.5 rounded text-purple-300">{pdfJobStatus["_all"].current_subject}</span>
                          </div>
                          <div className="text-white font-medium truncate">
                            📄 {pdfJobStatus["_all"].current_topic || "Starting..."}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            PDF #{pdfJobStatus["_all"].current_index || 1} of {pdfJobStatus["_all"].total}
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-white/10 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all ${pdfJobStatus["_all"].status === "paused" ? 'bg-amber-500' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`} 
                            style={{ width: `${(pdfJobStatus["_all"].completed / Math.max(1, pdfJobStatus["_all"].total)) * 100}%` }} 
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          <span>{Math.round((pdfJobStatus["_all"].completed / Math.max(1, pdfJobStatus["_all"].total)) * 100)}% complete</span>
                          <span>~{Math.ceil((pdfJobStatus["_all"].total - pdfJobStatus["_all"].completed) * 2 / 60)} min remaining</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Level 2: Subjects */}
        {selectedExam && !selectedSubject && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <FolderPlus className="w-6 h-6 text-indigo-400" />
                {selectedExam.name} - Subjects
              </h2>
              <Dialog open={showAddSubject} onOpenChange={setShowAddSubject}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-subject-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Subject
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-white/20 text-white">
                  <DialogHeader>
                    <DialogTitle>Add New Subject</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Subject ID (e.g., physics, mathematics)</label>
                      <Input
                        value={newSubjectId}
                        onChange={(e) => setNewSubjectId(e.target.value.toLowerCase().replace(/\s/g, '_'))}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="subject_id"
                        data-testid="new-subject-id"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Display Name (optional)</label>
                      <Input
                        value={newSubjectName}
                        onChange={(e) => setNewSubjectName(e.target.value)}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Physics"
                      />
                    </div>
                    <Button onClick={addSubject} className="w-full bg-indigo-600 hover:bg-indigo-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Subject
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
              </div>
            ) : subjects.length === 0 ? (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="py-12 text-center">
                  <FolderPlus className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400">No subjects yet. Add your first subject!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {subjects.map((subject) => (
                  <Card 
                    key={subject.id} 
                    className="bg-white/5 border-white/10 hover:bg-white/10 cursor-pointer transition-all group"
                    onClick={() => setSelectedSubject(subject)}
                    data-testid={`subject-card-${subject.id}`}
                  >
                    <CardContent className="py-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-purple-600/30 rounded-xl flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-purple-400" />
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setRenameTarget(subject);
                              setNewDisplayName(subject.name);
                              setShowRenameSubject(true);
                            }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={(e) => { e.stopPropagation(); deleteSubject(subject.id); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{subject.name}</h3>
                      <div className="text-sm text-gray-400">
                        {subject.topics_count} Topics
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Level 3: Topics */}
        {selectedExam && selectedSubject && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <FileText className="w-6 h-6 text-indigo-400" />
                {selectedSubject.name} - Topics ({topics.length})
              </h2>
              <div className="flex gap-2">
                {/* Import Topics Dialog */}
                <Dialog open={showAddTopics} onOpenChange={setShowAddTopics}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="import-topics-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Import Topics
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-white/20 text-white max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Import Topics</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">Paste topics (one per line)</label>
                        <Textarea
                          value={topicsText}
                          onChange={(e) => setTopicsText(e.target.value)}
                          className="bg-white/10 border-white/20 text-white min-h-[300px] font-mono text-sm"
                          placeholder="Topic 1 : Subtopic A&#10;Topic 1 : Subtopic B&#10;Topic 2 : Subtopic A&#10;..."
                          data-testid="topics-textarea"
                        />
                      </div>
                      <Button onClick={importTopics} className="w-full bg-indigo-600 hover:bg-indigo-700">
                        <Upload className="w-4 h-4 mr-2" />
                        Import Topics
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Generate with AI Dialog */}
                <Dialog open={showGenerateTopics} onOpenChange={setShowGenerateTopics}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-testid="generate-ai-btn">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate with AI
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-white/20 text-white max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-pink-400" />
                        Generate Topics with AI
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">Description/Context (optional)</label>
                        <Textarea
                          value={aiDescription}
                          onChange={(e) => setAiDescription(e.target.value)}
                          className="bg-white/10 border-white/20 text-white min-h-[100px]"
                          placeholder="Describe what topics you want... e.g., 'Complete syllabus for Class 12 Physics as per CBSE'"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">Number of Topics</label>
                        <Input
                          type="number"
                          value={aiTopicCount}
                          onChange={(e) => setAiTopicCount(parseInt(e.target.value) || 20)}
                          className="bg-white/10 border-white/20 text-white w-32"
                          min="5"
                          max="100"
                        />
                      </div>
                      <Button 
                        onClick={generateTopicsAI} 
                        disabled={generating}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      >
                        {generating ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" />Generate Topics</>
                        )}
                      </Button>

                      {/* Generated Topics Preview */}
                      {generatedTopics.length > 0 && (
                        <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-gray-300">Generated Topics ({generatedTopics.length})</h4>
                            <Button onClick={saveGeneratedTopics} size="sm" className="bg-green-600 hover:bg-green-700">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Save All
                            </Button>
                          </div>
                          <div className="max-h-[300px] overflow-y-auto space-y-1">
                            {generatedTopics.map((topic, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-gray-300 py-1 px-2 hover:bg-white/5 rounded">
                                <span className="text-gray-500 w-6">{i + 1}.</span>
                                <span className="flex-1">{topic}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-red-400 hover:text-red-300"
                                  onClick={() => setGeneratedTopics(prev => prev.filter((_, idx) => idx !== i))}
                                >
                                  <XCircle className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Extract Topics with AI (Text or Image) */}
                <Dialog open={showExtractTopics} onOpenChange={(open) => { if (!open) clearExtractForm(); else setShowExtractTopics(true); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700" data-testid="extract-ai-btn">
                      <FileText className="w-4 h-4 mr-2" />
                      Extract from Text/Image
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-white/20 text-white max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-emerald-400" />
                        Extract Topics with AI
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <p className="text-sm text-gray-400">
                        Upload a photo of syllabus/textbook index OR paste syllabus text. Gemini AI will extract topics and add them immediately.
                      </p>
                      
                      {/* Image Upload */}
                      <div>
                        <label className="text-sm text-gray-400 mb-2 block">Upload Syllabus Image (photo of syllabus, textbook index, etc.)</label>
                        <div className="flex items-center gap-3">
                          <label className="flex-1 cursor-pointer">
                            <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${extractImage ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/20 hover:border-white/40'}`}>
                              {extractImage ? (
                                <div className="flex items-center justify-center gap-2">
                                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                                  <span className="text-emerald-300">{extractImage.name}</span>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="ml-2 text-red-400 hover:text-red-300 h-6"
                                    onClick={(e) => { e.preventDefault(); setExtractImage(null); }}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="text-gray-400">
                                  <Upload className="w-8 h-8 mx-auto mb-2" />
                                  <span>Click to upload image</span>
                                </div>
                              )}
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => setExtractImage(e.target.files[0])}
                              data-testid="extract-image-input"
                            />
                          </label>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-white/20"></div>
                        <span className="text-gray-500 text-sm">OR</span>
                        <div className="flex-1 h-px bg-white/20"></div>
                      </div>
                      
                      {/* Text Input */}
                      <div>
                        <label className="text-sm text-gray-400 mb-1 block">Paste Syllabus Text</label>
                        <Textarea
                          value={extractText}
                          onChange={(e) => setExtractText(e.target.value)}
                          className="bg-white/10 border-white/20 text-white min-h-[150px] font-mono text-sm"
                          placeholder="Paste syllabus text, chapter names, unit descriptions...&#10;&#10;Example:&#10;Unit 1: Mechanics&#10;- Kinematics&#10;- Newton's Laws&#10;Unit 2: Thermodynamics&#10;- Heat Transfer&#10;- Laws of Thermodynamics"
                          data-testid="extract-text-input"
                        />
                      </div>
                      
                      <Button 
                        onClick={extractTopicsAI} 
                        disabled={extracting || (!extractText && !extractImage)}
                        className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                        data-testid="extract-topics-submit"
                      >
                        {extracting ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Extracting Topics...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" />Extract & Add Topics</>
                        )}
                      </Button>
                      
                      {/* Extracted Topics Preview */}
                      {extractedTopics.length > 0 && (
                        <div className="mt-4 p-4 bg-white/5 rounded-lg border border-emerald-500/30">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-emerald-300">
                              <CheckCircle className="w-4 h-4 inline mr-1" />
                              Extracted & Added: {extractedTopics.length} topics
                            </h4>
                          </div>
                          <div className="max-h-[200px] overflow-y-auto space-y-1">
                            {extractedTopics.map((topic, i) => (
                              <div key={i} className="text-sm text-gray-300 py-1 px-2 hover:bg-white/5 rounded">
                                <span className="text-gray-500 mr-2">{i + 1}.</span>
                                {topic}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Clear All Button */}
            {topics.length > 0 && (
              <div className="mb-4 flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={clearAllTopics}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Topics
                </Button>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
              </div>
            ) : topics.length === 0 ? (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400 mb-4">No topics yet. Import topics or generate with AI!</p>
                  <div className="flex gap-4 justify-center">
                    <Button onClick={() => setShowAddTopics(true)} variant="outline" className="border-white/20 text-white">
                      <Upload className="w-4 h-4 mr-2" />
                      Import
                    </Button>
                    <Button onClick={() => setShowGenerateTopics(true)} className="bg-gradient-to-r from-purple-600 to-pink-600">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate with AI
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-0">
                  <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
                    {topics.map((topic, index) => (
                      <div 
                        key={index}
                        data-testid={`topic-row-${index}`}
                      >
                        <div className="flex items-center gap-4 px-4 py-3 hover:bg-white/5 group">
                          <span className="text-gray-500 text-sm w-8">{index + 1}.</span>
                          <span className="flex-1 text-white text-sm">{topic}</span>
                          <button
                            onClick={() => setEditingTopicIndex(editingTopicIndex === topic ? null : topic)}
                            className={`text-xs px-2 py-1 rounded border transition-colors ${topicIndexes[topic] ? 'border-indigo-500 text-indigo-300 bg-indigo-500/10' : 'border-white/20 text-gray-400 hover:border-indigo-400 hover:text-indigo-300'}`}
                            title="Set AI generation instructions for this topic"
                          >
                            {topicIndexes[topic] ? 'Index Set' : 'Set Index'}
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => deleteTopic(topic)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {editingTopicIndex === topic && (
                          <div className="px-12 pb-4 bg-white/2 border-t border-white/5">
                            <p className="text-xs text-gray-400 mt-2 mb-1">AI Index — custom instructions for notes generation on this topic:</p>
                            <Textarea
                              value={topicIndexes[topic] || ""}
                              onChange={(e) => setTopicIndexes(prev => ({ ...prev, [topic]: e.target.value }))}
                              placeholder="e.g., Focus on Class 12 NCERT Chapter 6. Include Faraday's Law derivation, Lenz's Law with examples. Do NOT cover AC generators (covered in another topic)."
                              className="bg-white/10 border-white/20 text-white text-sm min-h-[80px] resize-none"
                            />
                            <div className="flex gap-2 mt-2">
                              <Button
                                size="sm"
                                onClick={() => saveTopicIndex(topic)}
                                disabled={savingTopicIndex === topic}
                                className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs"
                              >
                                {savingTopicIndex === topic ? 'Saving...' : 'Save Index'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingTopicIndex(null)}
                                className="text-gray-400 h-7 text-xs"
                              >
                                Close
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
          </TabsContent>

          {/* ===== NCERT EXEMPLAR TAB ===== */}
          <TabsContent value="ncert" className="mt-0">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <BookMarked className="w-6 h-6 text-orange-400" />
                    NCERT Exemplar Questions
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Add and manage NCERT Exemplar questions for TGT (Class 6-10) and PGT (Class 9-12)
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowNCERTExtractDialog(true)}
                    className="bg-orange-600 hover:bg-orange-700"
                    disabled={!ncertExamType || !ncertSubject || !ncertClass}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Extract from Text
                  </Button>
                  <Button
                    onClick={fetchQuestionsFromAI}
                    variant="outline"
                    className="border-orange-500 text-orange-400 hover:bg-orange-500/20"
                    disabled={!ncertExamType || !ncertSubject || fetchingNCERT}
                  >
                    {fetchingNCERT ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    {ncertClass ? 'AI Generate' : 'AI Generate All Classes'}
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Exam Type</label>
                      <select
                        value={ncertExamType}
                        onChange={(e) => {
                          setNcertExamType(e.target.value);
                          setNcertSubject('');
                          setNcertClass('');
                        }}
                        className="w-full bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white"
                      >
                        <option value="">Select Exam</option>
                        <option value="tgt">TGT (Class 6-10)</option>
                        <option value="pgt">PGT (Class 9-12)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Subject</label>
                      <select
                        value={ncertSubject}
                        onChange={(e) => setNcertSubject(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white"
                        disabled={!ncertExamType}
                      >
                        <option value="">Select Subject</option>
                        {ncertSubjectOptions.map((sub) => (
                          <option key={sub.id} value={sub.id}>{sub.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Class</label>
                      <select
                        value={ncertClass}
                        onChange={(e) => setNcertClass(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white"
                        disabled={!ncertExamType}
                      >
                        <option value="">All Classes</option>
                        {ncertClasses.map((cls) => (
                          <option key={cls} value={cls}>Class {cls}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats */}
              {ncertStats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(ncertStats.classes || {}).map(([cls, data]) => (
                    <Card key={cls} className="bg-white/5 border-white/10">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-orange-400">{data.question_count}</div>
                        <div className="text-sm text-gray-400">Class {cls}</div>
                        <div className="text-xs text-gray-500">{data.chapter_count} chapters</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Questions List */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span>Questions ({ncertQuestions.length})</span>
                    <Button
                      onClick={fetchNcertQuestions}
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-white"
                    >
                      <RefreshCw className={`w-4 h-4 ${ncertLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ncertLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
                    </div>
                  ) : ncertQuestions.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <BookMarked className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No questions found</p>
                      <p className="text-sm mt-2">Select exam, subject & class, then add questions</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {ncertQuestions.map((q, idx) => (
                        <div
                          key={q.id || idx}
                          className="p-4 bg-white/5 rounded-lg border border-white/10"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex gap-2">
                              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                                Class {q.class_level}
                              </Badge>
                              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                                {q.chapter}
                              </Badge>
                              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                                {q.marks}M
                              </Badge>
                            </div>
                            <Button
                              onClick={() => deleteNcertQuestion(q.id)}
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-white text-sm">{q.question_text}</p>
                          {q.answer && (
                            <p className="text-green-400 text-xs mt-2 line-clamp-2">
                              Answer: {q.answer}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info Box */}
              <Card className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-orange-500/30">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CircleHelp className="w-5 h-5 text-orange-400" />
                    How to Add NCERT Questions
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="text-orange-400 font-medium mb-1">1. Extract from Text</div>
                      <p className="text-gray-400">Paste NCERT book content - AI extracts questions automatically</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="text-orange-400 font-medium mb-1">2. AI Generate</div>
                      <p className="text-gray-400">Let AI generate NCERT-style questions for the selected class/subject</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===== NOTIFICATIONS TAB ===== */}
          <TabsContent value="notifications" className="mt-0 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-gradient-to-br from-pink-600 to-pink-800 border-0">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Bell className="w-8 h-8 text-white/80" />
                    <div>
                      <p className="text-2xl font-bold text-white">{notificationStats?.total_notifications || 0}</p>
                      <p className="text-pink-200 text-sm">Total Sent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-600 to-purple-800 border-0">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-white/80" />
                    <div>
                      <p className="text-2xl font-bold text-white">{notificationStats?.total_registered_devices || 0}</p>
                      <p className="text-purple-200 text-sm">Registered Devices</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-indigo-600 to-indigo-800 border-0">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Globe className="w-8 h-8 text-white/80" />
                    <div>
                      <p className="text-2xl font-bold text-white">{notificationStats?.recent_notifications?.length || 0}</p>
                      <p className="text-indigo-200 text-sm">This Week</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Send Notification Form */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Send className="w-5 h-5 text-pink-400" />
                    Send Push Notification
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Notification Type Selection */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Notification Type</label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => setNotificationForm({...notificationForm, notification_type: 'text'})}
                        className={`p-3 rounded-lg border flex flex-col items-center gap-1 transition-all ${
                          notificationForm.notification_type === 'text' 
                            ? 'bg-pink-600 border-pink-500 text-white' 
                            : 'bg-white/5 border-white/10 text-gray-400 hover:border-pink-500/50'
                        }`}
                      >
                        <FileText className="w-5 h-5" />
                        <span className="text-xs">Text Only</span>
                      </button>
                      <button
                        onClick={() => setNotificationForm({...notificationForm, notification_type: 'link'})}
                        className={`p-3 rounded-lg border flex flex-col items-center gap-1 transition-all ${
                          notificationForm.notification_type === 'link' 
                            ? 'bg-pink-600 border-pink-500 text-white' 
                            : 'bg-white/5 border-white/10 text-gray-400 hover:border-pink-500/50'
                        }`}
                      >
                        <LinkIcon className="w-5 h-5" />
                        <span className="text-xs">Text + Link</span>
                      </button>
                      <button
                        onClick={() => setNotificationForm({...notificationForm, notification_type: 'link_image'})}
                        className={`p-3 rounded-lg border flex flex-col items-center gap-1 transition-all ${
                          notificationForm.notification_type === 'link_image' 
                            ? 'bg-pink-600 border-pink-500 text-white' 
                            : 'bg-white/5 border-white/10 text-gray-400 hover:border-pink-500/50'
                        }`}
                      >
                        <Image className="w-5 h-5" />
                        <span className="text-xs">Link + Image</span>
                      </button>
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Title *</label>
                    <Input
                      placeholder="Notification title..."
                      value={notificationForm.title}
                      onChange={(e) => setNotificationForm({...notificationForm, title: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      maxLength={100}
                    />
                  </div>

                  {/* Body/Message */}
                  <div>
                    <label className="text-gray-400 text-sm mb-1 block">Message *</label>
                    <Textarea
                      placeholder="Notification message..."
                      value={notificationForm.body}
                      onChange={(e) => setNotificationForm({...notificationForm, body: e.target.value})}
                      className="bg-white/10 border-white/20 text-white min-h-[80px]"
                      maxLength={500}
                    />
                  </div>

                  {/* Link URL (for link and link_image types) */}
                  {(notificationForm.notification_type === 'link' || notificationForm.notification_type === 'link_image') && (
                    <div>
                      <label className="text-gray-400 text-sm mb-1 block">Link URL *</label>
                      <Input
                        placeholder="https://example.com/page"
                        value={notificationForm.link_url}
                        onChange={(e) => setNotificationForm({...notificationForm, link_url: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  )}

                  {/* Image URL (for link_image type) */}
                  {notificationForm.notification_type === 'link_image' && (
                    <div>
                      <label className="text-gray-400 text-sm mb-1 block">Thumbnail Image URL *</label>
                      <Input
                        placeholder="https://example.com/image.jpg"
                        value={notificationForm.image_url}
                        onChange={(e) => setNotificationForm({...notificationForm, image_url: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                      />
                      {notificationForm.image_url && (
                        <div className="mt-2 p-2 bg-black/20 rounded-lg">
                          <img 
                            src={notificationForm.image_url} 
                            alt="Preview" 
                            className="max-h-32 rounded object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Target Audience */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Target Audience</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setNotificationForm({...notificationForm, target_audience: 'all'})}
                        className={`px-4 py-2 rounded-lg text-sm transition-all ${
                          notificationForm.target_audience === 'all' 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-white/10 text-gray-400 hover:bg-white/20'
                        }`}
                      >
                        All Users
                      </button>
                      <button
                        onClick={() => setNotificationForm({...notificationForm, target_audience: 'paid'})}
                        className={`px-4 py-2 rounded-lg text-sm transition-all ${
                          notificationForm.target_audience === 'paid' 
                            ? 'bg-green-600 text-white' 
                            : 'bg-white/10 text-gray-400 hover:bg-white/20'
                        }`}
                      >
                        Paid Only
                      </button>
                      <button
                        onClick={() => setNotificationForm({...notificationForm, target_audience: 'free'})}
                        className={`px-4 py-2 rounded-lg text-sm transition-all ${
                          notificationForm.target_audience === 'free' 
                            ? 'bg-yellow-600 text-white' 
                            : 'bg-white/10 text-gray-400 hover:bg-white/20'
                        }`}
                      >
                        Free Only
                      </button>
                    </div>
                  </div>

                  {/* Send Button */}
                  <Button
                    onClick={sendNotification}
                    disabled={sendingNotification}
                    className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                  >
                    {sendingNotification ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Notification</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Notification History */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-400" />
                    Recent Notifications
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchNotificationHistory}
                    className="text-gray-400 hover:text-white"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingNotifications ? 'animate-spin' : ''}`} />
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingNotifications ? (
                    <div className="text-center py-8 text-gray-400">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading...
                    </div>
                  ) : notificationHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No notifications sent yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {notificationHistory.map((notif) => (
                        <div 
                          key={notif.notification_id} 
                          className="p-3 bg-white/5 rounded-lg border border-white/10"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{notif.title}</p>
                              <p className="text-gray-400 text-sm line-clamp-2">{notif.body}</p>
                              <div className="flex items-center gap-3 mt-2">
                                <Badge variant="outline" className={`text-xs ${
                                  notif.notification_type === 'text' ? 'border-gray-500 text-gray-400' :
                                  notif.notification_type === 'link' ? 'border-blue-500 text-blue-400' :
                                  'border-purple-500 text-purple-400'
                                }`}>
                                  {notif.notification_type === 'text' ? 'Text' : 
                                   notif.notification_type === 'link' ? 'Link' : 'Image'}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  {new Date(notif.created_at).toLocaleDateString()}
                                </span>
                                <span className="text-xs text-green-400">
                                  {notif.sent_count} sent
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => deleteNotification(notif.notification_id)}
                              className="text-gray-500 hover:text-red-400 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===== TEAM MANAGEMENT TAB ===== */}
          <TabsContent value="team" className="mt-0 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-gradient-to-br from-green-600/20 to-green-800/20 border-green-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-500/20 rounded-xl">
                      <Users className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">{teamMembers.length}</p>
                      <p className="text-green-300 text-sm">Total Members</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border-blue-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-500/20 rounded-xl">
                      <GraduationCap className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">
                        {teamMembers.filter(m => m.role === 'teacher').length}
                      </p>
                      <p className="text-blue-300 text-sm">Teachers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border-purple-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-500/20 rounded-xl">
                      <Shield className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">
                        {teamMembers.filter(m => m.role === 'support').length}
                      </p>
                      <p className="text-purple-300 text-sm">Support</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Add Member Card */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-400" />
                  Team Members
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchTeamMembers}
                    disabled={teamLoading}
                    className="text-white"
                  >
                    <RefreshCw className={`w-4 h-4 ${teamLoading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button
                    onClick={() => setShowAddMember(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {teamLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 text-green-400 animate-spin" />
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No team members added yet</p>
                    <p className="text-sm">Add teachers, support staff, or members</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {teamMembers.map((member) => (
                      <div key={member.mobile} className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl ${
                            member.role === 'teacher' ? 'bg-blue-500/20' :
                            member.role === 'support' ? 'bg-purple-500/20' : 'bg-gray-500/20'
                          }`}>
                            {member.role === 'teacher' ? <GraduationCap className="w-5 h-5 text-blue-400" /> :
                             member.role === 'support' ? <Shield className="w-5 h-5 text-purple-400" /> :
                             <Users className="w-5 h-5 text-gray-400" />}
                          </div>
                          <div>
                            <p className="text-white font-medium">{member.name}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Phone className="w-3 h-3" />
                              {member.mobile}
                              {member.is_registered && (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">Registered</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateRole(member.mobile, e.target.value)}
                            className="bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-white text-sm"
                          >
                            <option value="teacher">Teacher</option>
                            <option value="support">Support</option>
                            <option value="member">Member</option>
                          </select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member.mobile, member.name)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Member Dialog */}
            <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
              <DialogContent className="bg-slate-900 border-white/20 text-white">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-green-400" />
                    Add Team Member
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Mobile Number</label>
                    <Input
                      value={newMember.mobile}
                      onChange={(e) => setNewMember({...newMember, mobile: e.target.value})}
                      placeholder="Enter mobile number"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Name</label>
                    <Input
                      value={newMember.name}
                      onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                      placeholder="Enter name"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Role</label>
                    <select
                      value={newMember.role}
                      onChange={(e) => setNewMember({...newMember, role: e.target.value})}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="teacher">Teacher</option>
                      <option value="support">Support</option>
                      <option value="member">Member</option>
                    </select>
                  </div>
                  <Button
                    onClick={handleAddTeamMember}
                    disabled={addingMember}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {addingMember ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    Add Member
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ===== CHAT MONITORING TAB ===== */}
          <TabsContent value="chats" className="mt-0 space-y-6">
            {/* Passcode Gate */}
            {!chatPasscodeVerified ? (
              <div className="flex flex-col items-center justify-center py-24 space-y-6">
                <div className="p-4 bg-red-500/10 rounded-2xl border border-red-500/20">
                  <MessageCircle className="w-10 h-10 text-red-400" />
                </div>
                <div className="text-center">
                  <h3 className="text-white text-xl font-bold">Chat Monitor</h3>
                  <p className="text-gray-400 text-sm mt-1">Enter access code to view student chats</p>
                </div>
                <div className="flex gap-3 items-center">
                  <input
                    type="password"
                    value={chatPasscodeInput}
                    onChange={e => { setChatPasscodeInput(e.target.value); setChatPasscodeError(false); }}
                    onKeyDown={e => { if (e.key === 'Enter') {
                      if (btoa(chatPasscodeInput) === CHAT_PC) { setChatPasscodeVerified(true); }
                      else { setChatPasscodeError(true); setChatPasscodeInput(''); }
                    }}}
                    placeholder="Access code"
                    className={`bg-white/10 border ${chatPasscodeError ? 'border-red-500' : 'border-white/20'} rounded-xl px-5 py-3 text-white text-center tracking-widest text-lg w-48 focus:outline-none focus:border-red-400`}
                    maxLength={10}
                    autoFocus
                  />
                  <Button
                    onClick={() => {
                      if (btoa(chatPasscodeInput) === CHAT_PC) { setChatPasscodeVerified(true); }
                      else { setChatPasscodeError(true); setChatPasscodeInput(''); }
                    }}
                    className="bg-red-600 hover:bg-red-700 px-6 py-3"
                  >Unlock</Button>
                </div>
                {chatPasscodeError && <p className="text-red-400 text-sm">Incorrect code. Try again.</p>}
              </div>
            ) : (
            <div className="space-y-6">
            {/* ─── Support Chat Dashboard ─────────────────────────────────── */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-500/20 rounded-xl">
                    <MessageCircle className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base">Support Chat Dashboard</h3>
                    <p className="text-gray-400 text-xs">Student queries via the chat widget</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Period selector */}
                  <div className="flex gap-1 bg-white/5 border border-white/10 rounded-xl p-1">
                    {[["daily", "Today"], ["monthly", "Month"], ["yearly", "Year"]].map(([val, label]) => (
                      <button key={val}
                        onClick={() => { setSupportPeriod(val); fetchSupportStats(val); }}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                          supportPeriod === val ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"
                        }`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <a href="/chat" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 text-indigo-300 text-xs px-3 py-1.5 rounded-xl transition-all font-medium">
                    Open Chat <span className="opacity-60">↗</span>
                  </a>
                </div>
              </div>

              {supportStatsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {/* Live Doubts */}
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 relative overflow-hidden">
                    <div className="absolute top-3 right-3 w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                    <p className="text-3xl font-black text-amber-400">{supportStats?.period_stats?.live ?? 0}</p>
                    <p className="text-amber-300/80 text-xs font-semibold mt-1">Live Doubts</p>
                    <p className="text-amber-500/60 text-[10px] mt-0.5">All time: {supportStats?.all_time?.live ?? 0}</p>
                  </div>
                  {/* Replied */}
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                    <p className="text-3xl font-black text-green-400">{supportStats?.period_stats?.replied ?? 0}</p>
                    <p className="text-green-300/80 text-xs font-semibold mt-1">Replied</p>
                    <p className="text-green-500/60 text-[10px] mt-0.5">All time: {supportStats?.all_time?.replied ?? 0}</p>
                  </div>
                  {/* Closed */}
                  <div className="bg-slate-500/10 border border-slate-500/20 rounded-xl p-4">
                    <p className="text-3xl font-black text-slate-300">{supportStats?.period_stats?.closed ?? 0}</p>
                    <p className="text-slate-400/80 text-xs font-semibold mt-1">Closed</p>
                    <p className="text-slate-500/60 text-[10px] mt-0.5">All time: {supportStats?.all_time?.closed ?? 0}</p>
                  </div>
                  {/* Escalated */}
                  <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 relative">
                    {(supportStats?.all_time?.escalated ?? 0) > 0 && <span className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-rose-500 animate-ping" />}
                    <p className="text-3xl font-black text-rose-400">{supportStats?.period_stats?.escalated ?? 0}</p>
                    <p className="text-rose-300/80 text-xs font-semibold mt-1">Escalated</p>
                    <p className="text-rose-500/60 text-[10px] mt-0.5">All time: {supportStats?.all_time?.escalated ?? 0}</p>
                  </div>
                  {/* Total */}
                  <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4">
                    <p className="text-3xl font-black text-indigo-400">{supportStats?.period_stats?.total ?? 0}</p>
                    <p className="text-indigo-300/80 text-xs font-semibold mt-1">Total Chats</p>
                    <p className="text-indigo-500/60 text-[10px] mt-0.5">All time: {supportStats?.all_time?.total ?? 0}</p>
                  </div>
                </div>
              )}

              {/* Quick breakdown bar */}
              {!supportStatsLoading && supportStats?.all_time?.total > 0 && (
                <div className="mt-3">
                  <div className="flex gap-1 rounded-full overflow-hidden h-2">
                    {supportStats.all_time.live > 0      && <div className="bg-amber-500 transition-all" style={{ width: `${(supportStats.all_time.live / supportStats.all_time.total) * 100}%` }} />}
                    {supportStats.all_time.replied > 0   && <div className="bg-green-500 transition-all" style={{ width: `${(supportStats.all_time.replied / supportStats.all_time.total) * 100}%` }} />}
                    {supportStats.all_time.escalated > 0 && <div className="bg-rose-500 transition-all" style={{ width: `${(supportStats.all_time.escalated / supportStats.all_time.total) * 100}%` }} />}
                    {supportStats.all_time.closed > 0    && <div className="bg-slate-500 transition-all" style={{ width: `${(supportStats.all_time.closed / supportStats.all_time.total) * 100}%` }} />}
                  </div>
                  <div className="flex gap-4 mt-2">
                    <span className="flex items-center gap-1 text-[10px] text-amber-400"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block"/>Live</span>
                    <span className="flex items-center gap-1 text-[10px] text-green-400"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"/>Replied</span>
                    <span className="flex items-center gap-1 text-[10px] text-rose-400"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block"/>Escalated</span>
                    <span className="flex items-center gap-1 text-[10px] text-slate-400"><span className="w-2 h-2 rounded-full bg-slate-500 inline-block"/>Closed</span>
                  </div>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-red-600/20 to-red-800/20 border-red-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-red-500/20 rounded-xl">
                      <MessageCircle className="w-6 h-6 text-red-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">{chatStats?.messages_today || 0}</p>
                      <p className="text-red-300 text-sm">Messages Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border-blue-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-500/20 rounded-xl">
                      <Users className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">{chatStats?.active_chatters_today || 0}</p>
                      <p className="text-blue-300 text-sm">Active Chatters</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-600/20 to-green-800/20 border-green-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-500/20 rounded-xl">
                      <Activity className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">{chatStats?.currently_online || 0}</p>
                      <p className="text-green-300 text-sm">Currently Online</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-600/20 to-orange-800/20 border-orange-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-500/20 rounded-xl">
                      <Ban className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">{chatStats?.total_blocks || 0}</p>
                      <p className="text-orange-300 text-sm">Total Blocks</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Active Chats */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-red-400" />
                  Active Peer Chats (Last {chatTimeRange}h)
                </CardTitle>
                <div className="flex gap-2">
                  <select
                    value={chatTimeRange}
                    onChange={(e) => setChatTimeRange(Number(e.target.value))}
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-white text-sm"
                  >
                    <option value={6}>Last 6 hours</option>
                    <option value={12}>Last 12 hours</option>
                    <option value={24}>Last 24 hours</option>
                    <option value={48}>Last 48 hours</option>
                  </select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchActiveChats}
                    disabled={chatsLoading}
                    className="text-white"
                  >
                    <RefreshCw className={`w-4 h-4 ${chatsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {chatsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 text-red-400 animate-spin" />
                  </div>
                ) : activeChats.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No active chats in the selected time range</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeChats.map((chat, idx) => (
                      <div key={chat.chat_room} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                        <div 
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5"
                          onClick={() => setSelectedChat(selectedChat === idx ? null : idx)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex -space-x-2">
                              {chat.participants.slice(0, 2).map((p, i) => (
                                <div key={i} className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold border-2 border-slate-900">
                                  {p.name?.[0]?.toUpperCase() || '?'}
                                </div>
                              ))}
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {chat.participants.map(p => p.name || 'Unknown').join(' & ')}
                              </p>
                              <p className="text-gray-400 text-sm">
                                {chat.message_count} messages • Last: {new Date(chat.last_activity).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${selectedChat === idx ? 'rotate-90' : ''}`} />
                        </div>
                        
                        {selectedChat === idx && (
                          <div className="border-t border-white/10 p-4 bg-black/20 max-h-80 overflow-y-auto">
                            {chat.messages.map((msg, mIdx) => (
                              <div key={mIdx} className="mb-3 last:mb-0">
                                <div className="flex items-start gap-2">
                                  <span className="text-xs text-purple-400 font-medium min-w-[60px]">
                                    {chat.participants.find(p => p.id === msg.sender_id)?.name?.split(' ')[0] || 'User'}:
                                  </span>
                                  <p className="text-gray-300 text-sm flex-1">{msg.content}</p>
                                  <span className="text-xs text-gray-500">
                                    {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
            )}
          </TabsContent>
          <TabsContent value="cache" className="mt-0">
            <Card className="bg-white/5 border-white/10" data-testid="cache-management-card">
              <CardHeader>
                <CardTitle className="text-xl text-red-400 flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Notes Cache Management
                </CardTitle>
                <p className="text-sm text-slate-400">Permanently deletes notes from <span className="text-red-400 font-semibold">all 3 locations</span>: permanent storage, notes cache, and PDF cache. Filter by exam category, subject, or topic. Enter code <span className="text-yellow-400 font-mono font-bold">508969</span> to confirm.</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Stats Section */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <p className="text-sm text-blue-300">Cached Notes</p>
                    <p className="text-2xl font-bold text-white" data-testid="cached-notes-count">{cacheStats?.total_notes ?? "..."}</p>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                    <p className="text-sm text-purple-300">Cached PDFs</p>
                    <p className="text-2xl font-bold text-white" data-testid="cached-pdfs-count">{cacheStats?.total_pdfs ?? "..."}</p>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                    <p className="text-sm text-amber-300">Permanent Notes</p>
                    <p className="text-2xl font-bold text-white" data-testid="permanent-notes-count">{cacheStats?.total_permanent ?? "..."}</p>
                  </div>
                </div>

                {/* Category Breakdown */}
                {cacheStats?.categories && Object.keys(cacheStats.categories).length > 0 && (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-300 mb-3">Category Breakdown</h4>
                    <div className="space-y-2">
                      {Object.entries(cacheStats.categories).map(([et, subjects]) => (
                        <div key={et} className="border border-white/5 rounded p-3">
                          <p className="text-sm font-bold text-yellow-400 uppercase mb-2">{et}</p>
                          {Object.entries(subjects).map(([subj, data]) => (
                            <div key={subj} className="ml-4 mb-1 text-xs text-slate-400">
                              <span className="text-slate-200">{subj}</span> — {data.count} notes
                              <span className="text-slate-500 ml-2">({data.topics.length} topics)</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delete Filters */}
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 space-y-4">
                  <h4 className="text-sm font-semibold text-red-300">Bulk Delete</h4>
                  <p className="text-xs text-slate-400">Leave filters empty to delete ALL notes from all 3 collections (permanent + cache + PDFs).</p>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Exam Category</label>
                      <select 
                        data-testid="cache-delete-exam-type"
                        className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm text-white"
                        value={deleteFilter.exam_type}
                        onChange={(e) => setDeleteFilter(prev => ({...prev, exam_type: e.target.value, subject: "", topic: ""}))}
                      >
                        <option value="">All Categories</option>
                        {cacheStats?.categories && Object.keys(cacheStats.categories).map(et => (
                          <option key={et} value={et}>{et.toUpperCase()}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Subject</label>
                      <select 
                        data-testid="cache-delete-subject"
                        className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm text-white"
                        value={deleteFilter.subject}
                        onChange={(e) => setDeleteFilter(prev => ({...prev, subject: e.target.value, topic: ""}))}
                      >
                        <option value="">All Subjects</option>
                        {deleteFilter.exam_type && cacheStats?.categories?.[deleteFilter.exam_type] && 
                          Object.keys(cacheStats.categories[deleteFilter.exam_type]).map(subj => (
                            <option key={subj} value={subj}>{subj}</option>
                          ))
                        }
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Topic</label>
                      <select 
                        data-testid="cache-delete-topic"
                        className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm text-white"
                        value={deleteFilter.topic}
                        onChange={(e) => setDeleteFilter(prev => ({...prev, topic: e.target.value}))}
                      >
                        <option value="">All Topics</option>
                        {deleteFilter.exam_type && deleteFilter.subject && 
                          cacheStats?.categories?.[deleteFilter.exam_type]?.[deleteFilter.subject]?.topics?.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))
                        }
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="text-xs text-slate-400 mb-1 block">Confirmation Code</label>
                      <input
                        data-testid="cache-delete-code-input"
                        type="password"
                        placeholder="Enter code to confirm"
                        className="w-full bg-slate-800 border border-white/10 rounded px-3 py-2 text-sm text-white font-mono"
                        value={deleteCode}
                        onChange={(e) => setDeleteCode(e.target.value)}
                      />
                    </div>
                    <button
                      data-testid="cache-bulk-delete-btn"
                      onClick={handleBulkDelete}
                      disabled={deleting || !deleteCode}
                      className="mt-5 px-6 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded font-semibold text-sm flex items-center gap-2 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      {deleting ? "Deleting..." : "Delete Notes"}
                    </button>
                  </div>

                  {/* Delete Result */}
                  {deleteResult && (
                    <div className={`p-3 rounded text-sm ${deleteResult.success ? "bg-green-500/10 border border-green-500/20 text-green-300" : "bg-red-500/10 border border-red-500/20 text-red-300"}`}>
                      {deleteResult.success 
                        ? `✅ Deleted: ${deleteResult.permanent_notes_deleted} permanent notes, ${deleteResult.notes_deleted} cached notes, ${deleteResult.pdfs_deleted} PDFs. Filter: ${typeof deleteResult.filter_used === 'string' ? deleteResult.filter_used : JSON.stringify(deleteResult.filter_used)}`
                        : `Error: ${deleteResult.error}`
                      }
                    </div>
                  )}
                </div>

                <button onClick={fetchCacheStats} disabled={cacheLoading} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm">
                  {cacheLoading ? "Loading..." : "Refresh Stats"}
                </button>
              </CardContent>
            </Card>

            {/* ── Language Quality Scanner ──────────────────────────────── */}
            <LanguageQualityScanner lmsPassword={LMS_PASSWORD} API={API} />

          </TabsContent>

          {/* ═══ PAYMENTS SYNC TAB ═══ */}
          <TabsContent value="payments-sync" className="mt-0">
            <PaymentsSyncTab />
          </TabsContent>

          <TabsContent value="mock-tests" className="mt-0">
            <MockTestsTab />
          </TabsContent>

        </Tabs>
        <Dialog open={showNCERTExtractDialog} onOpenChange={setShowNCERTExtractDialog}>
          <DialogContent className="bg-slate-900 border-white/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookMarked className="w-5 h-5 text-orange-400" />
                Extract NCERT Questions from Text
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {/* Selection Info */}
              <div className="flex gap-2 text-sm">
                <Badge className="bg-orange-500/20 text-orange-400">
                  {ncertExamType?.toUpperCase()}
                </Badge>
                <Badge className="bg-blue-500/20 text-blue-400">
                  {ncertSubject}
                </Badge>
                <Badge className="bg-green-500/20 text-green-400">
                  Class {ncertClass}
                </Badge>
              </div>

              {/* Text Input */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">
                  Paste NCERT Book Content / Questions
                </label>
                <Textarea
                  value={ncertExtractText}
                  onChange={(e) => setNcertExtractText(e.target.value)}
                  placeholder="Paste NCERT Exemplar questions here... AI will extract and format them."
                  className="bg-white/10 border-white/20 text-white min-h-[200px]"
                />
              </div>

              {/* Extract Button */}
              <Button
                onClick={extractNcertQuestionsFromText}
                disabled={extractingNCERT || !ncertExtractText.trim()}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                {extractingNCERT ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Extract Questions
                  </>
                )}
              </Button>

              {/* Extracted Questions Preview */}
              {extractedQuestions.length > 0 && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-white">
                      Extracted Questions ({extractedQuestions.length})
                    </h4>
                    <Button
                      onClick={addExtractedQuestions}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Add All to Database
                    </Button>
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {extractedQuestions.map((q, idx) => (
                      <div key={idx} className="p-3 bg-white/5 rounded-lg text-sm">
                        <div className="flex gap-2 mb-1">
                          <Badge className="text-xs bg-blue-500/20 text-blue-400">
                            {q.chapter}
                          </Badge>
                          <Badge className="text-xs bg-gray-500/20 text-gray-400">
                            {q.marks || 5}M
                          </Badge>
                        </div>
                        <p className="text-gray-200">{q.question_text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* AI Training Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="bg-slate-900 border-white/20 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-400" />
                Upload Training Material
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {/* Material Type */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Material Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={trainingForm.material_type === 'pyq' ? 'default' : 'outline'}
                    className={trainingForm.material_type === 'pyq' ? 'bg-blue-600' : 'border-white/20'}
                    onClick={() => setTrainingForm({ ...trainingForm, material_type: 'pyq' })}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    PYQ Paper
                  </Button>
                  <Button
                    type="button"
                    variant={trainingForm.material_type === 'notes' ? 'default' : 'outline'}
                    className={trainingForm.material_type === 'notes' ? 'bg-green-600' : 'border-white/20'}
                    onClick={() => setTrainingForm({ ...trainingForm, material_type: 'notes' })}
                  >
                    <BookMarked className="w-4 h-4 mr-2" />
                    Reference Notes
                  </Button>
                </div>
              </div>

              {/* Exam Type */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Exam Type</label>
                <select
                  value={trainingForm.exam_type}
                  onChange={(e) => setTrainingForm({ ...trainingForm, exam_type: e.target.value, subject: '' })}
                  className="w-full bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white"
                >
                  <option value="">Select Exam</option>
                  {exams.map((exam) => (
                    <option key={exam.id} value={exam.id}>{exam.name}</option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Subject</label>
                <select
                  value={trainingForm.subject}
                  onChange={(e) => setTrainingForm({ ...trainingForm, subject: e.target.value })}
                  className="w-full bg-white/10 border border-white/20 rounded-md px-3 py-2 text-white"
                  disabled={!trainingForm.exam_type}
                >
                  <option value="">Select Subject</option>
                  {trainingSubjectOptions.map((sub) => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Title</label>
                <Input
                  value={trainingForm.title}
                  onChange={(e) => setTrainingForm({ ...trainingForm, title: e.target.value })}
                  placeholder="e.g., TGT Science PYQ 2023"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">File (PDF, PNG, JPG)</label>
                <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center hover:border-purple-500/50 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.txt,.md"
                    onChange={(e) => setTrainingFile(e.target.files[0])}
                    className="hidden"
                    id="training-file-input"
                  />
                  <label htmlFor="training-file-input" className="cursor-pointer">
                    {trainingFile ? (
                      <div className="flex items-center justify-center gap-2 text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        {trainingFile.name}
                      </div>
                    ) : (
                      <div className="text-gray-400">
                        <FileUp className="w-8 h-8 mx-auto mb-2" />
                        <p>Click to select file</p>
                        <p className="text-xs mt-1">Max 10MB</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Submit */}
              <Button
                onClick={uploadTrainingMaterial}
                disabled={uploadingTraining || !trainingFile || !trainingForm.exam_type || !trainingForm.subject || !trainingForm.title}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {uploadingTraining ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload & Extract
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Rename Exam Dialog */}
        <Dialog open={showRenameExam} onOpenChange={setShowRenameExam}>
          <DialogContent className="bg-slate-900 border-white/20 text-white">
            <DialogHeader>
              <DialogTitle>Rename Exam Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Current ID: {renameTarget?.id}</label>
                <Input
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="New display name"
                />
              </div>
              <Button 
                onClick={renameExam} 
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Save New Name
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Rename Subject Dialog */}
        <Dialog open={showRenameSubject} onOpenChange={setShowRenameSubject}>
          <DialogContent className="bg-slate-900 border-white/20 text-white">
            <DialogHeader>
              <DialogTitle>Rename Subject</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Current ID: {renameTarget?.id}</label>
                <Input
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="New display name"
                />
              </div>
              <Button 
                onClick={renameSubject} 
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Save New Name
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
      <AdminBot />
    </div>
  );
}
